/**
 * webwebweb.dev - Web Application Entry Point
 * A web visualizer of web apps into a web of nodes
 */

// Buffer polyfill for browser (needed by isomorphic-git)
import { Buffer } from 'buffer';
(window as any).Buffer = Buffer;

import {
  getSession,
  refreshSession,
  signIn,
  signOut,
  isPro,
  getAccessToken,
  type Session,
  type User
} from './lib/auth';
import { getUserRepos, type GitHubRepo } from './git/auth';
import { cloneRepo, readAllRepoFiles, isRepoCloned, deleteRepo, listClonedRepos, resetFS } from './git/clone';
import { buildTimelapseFromGitHub } from './git/history';
import { openDirectoryPicker, hasFileSystemAccess, setupDropZone, readInputFiles } from './input/local';
import { scanFileMap, shouldIncludeFile } from '../core/scanner';
import { buildDependencyGraph } from '../core/graph-builder';
import { initGraph, toggleLabels, toggleOrphans, setClusterMode, setSizeMode, zoomToFit, searchNodes, destroyGraph, unpinAll, groupColors, exportToSVG, exportToPNG, getNodePositions, applyNodePositions } from './visualization/graph';
import type { DependencyGraph, ProgressCallback, FileHistory, CommitInfo, GraphNode } from '../core/types';

// Safe HTML setter using template element for sanitization
function setHTML(element: HTMLElement, html: string): void {
  const template = document.createElement('template');
  template.innerHTML = html.trim();
  element.replaceChildren(...Array.from(template.content.childNodes));
}

// Timelapse state
interface TimelapseState {
  sortedCommits: CommitInfo[];  // oldest first
  filesByCommit: Map<string, Set<string>>;  // commitOid -> set of file paths
  changesByCommit: Map<string, { added: string[], modified: string[], deleted: string[] }>;
  currentVisibleFiles: Set<string>;
  recentlyAdded: Set<string>;
  recentlyModified: Set<string>;
  recentlyDeleted: Set<string>;
  playbackSpeed: number;
  playbackTimer: number | null;
  isReady: boolean;
  isPrecomputing: boolean;
}

// App state
interface AppState {
  session: Session | null;
  view: 'landing' | 'repos' | 'loading' | 'visualization' | 'projects' | 'shared';
  isViewingShared: boolean;  // True when viewing someone else's shared project
  sharedProjectInfo: { fullName: string; ownerUsername: string; ownerAvatar: string } | null;
  repos: GitHubRepo[];
  selectedRepo: GitHubRepo | null;
  graph: DependencyGraph | null;
  commits: CommitInfo[];
  fileHistories: Map<string, FileHistory>;
  currentCommitIndex: number;
  isPlaying: boolean;
  progress: { phase: string; current: number; total: number; message?: string } | null;
  error: string | null;
  success: string | null;
  timelapse: TimelapseState;
  savedProjects: SavedProject[];
  currentProjectId: string | null;
  savedNodePositions: Record<string, { x: number; y: number; fx: number | null; fy: number | null }> | null;
  nodeAnnotations: Record<string, string>;  // nodeId -> note text
  currentProjectSettings: ProjectSettings | null;  // Settings to apply when graph loads
}

interface ProjectSettings {
  defaultView: 'graph' | 'tree' | 'metrics';
  defaultClustering: 'none' | 'type' | 'folder';
  showLabels: boolean;
  hideOrphans: boolean;
  sizeMode: 'lines' | 'connections';
}

interface SavedProject {
  id: string;
  fullName: string;
  owner: string;
  repo: string;
  hasWebhook: boolean;
  lastAnalyzedAt: string | null;
  status: string;
  stats: { nodeCount: number; edgeCount: number; totalLines: number } | null;
  isPublic: boolean;
  shareToken: string | null;
  description: string | null;
  settings: ProjectSettings;
}

const state: AppState = {
  session: null,
  view: 'landing',
  isViewingShared: false,
  sharedProjectInfo: null,
  repos: [],
  selectedRepo: null,
  graph: null,
  commits: [],
  fileHistories: new Map(),
  currentCommitIndex: 0,
  isPlaying: false,
  progress: null,
  error: null,
  success: null,
  timelapse: {
    sortedCommits: [],
    filesByCommit: new Map(),
    changesByCommit: new Map(),
    currentVisibleFiles: new Set(),
    recentlyAdded: new Set(),
    recentlyModified: new Set(),
    recentlyDeleted: new Set(),
    playbackSpeed: 400,
    playbackTimer: null,
    isReady: false,
    isPrecomputing: false
  },
  savedProjects: [],
  currentProjectId: null,
  savedNodePositions: null,
  nodeAnnotations: {},
  currentProjectSettings: null
};

// DOM Elements
let app: HTMLElement;

/**
 * Initialize the application
 */
async function init() {
  app = document.getElementById('app')!;

  // Check for share URL (/s/:token)
  const path = window.location.pathname;
  const shareMatch = path.match(/^\/s\/([a-zA-Z0-9]+)$/);

  if (shareMatch) {
    const shareToken = shareMatch[1];
    await loadSharedProject(shareToken);
    return;
  }

  // Check URL params for messages
  const urlParams = new URLSearchParams(window.location.search);
  const justUpgraded = urlParams.get('upgraded') === 'true';

  if (justUpgraded) {
    state.success = 'Welcome to Pro! You now have access to GitHub integration.';
    window.history.replaceState({}, '', '/');
  }
  if (urlParams.get('canceled') === 'true') {
    state.error = 'Subscription canceled. You can upgrade anytime.';
    window.history.replaceState({}, '', '/');
  }
  if (urlParams.get('error')) {
    state.error = urlParams.get('error') || 'An error occurred';
    window.history.replaceState({}, '', '/');
  }

  // Get session from server (force refresh if just upgraded to get new Pro status)
  if (justUpgraded) {
    state.session = await refreshSession();
  } else {
    state.session = await getSession();
  }

  // Hide loading spinner
  const loading = document.getElementById('loading');
  if (loading) loading.style.display = 'none';

  // Render initial view
  render();
}

/**
 * Main render function
 */
function render() {
  switch (state.view) {
    case 'landing':
      renderLanding();
      break;
    case 'projects':
      renderProjects();
      break;
    case 'repos':
      renderRepoSelector();
      break;
    case 'loading':
      renderLoading();
      break;
    case 'visualization':
      renderVisualization();
      break;
    case 'shared':
      renderSharedVisualization();
      break;
  }
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHTML(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

/**
 * Render landing page
 */
const GITHUB_ICON = `<svg viewBox="0 0 16 16"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>`;

// App logo - interconnected nodes forming a web
const APP_LOGO = `<svg viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="18" cy="6" r="3.5" fill="#00d4ff"/>
  <circle cx="6" cy="26" r="3.5" fill="#bf5af2"/>
  <circle cx="30" cy="26" r="3.5" fill="#30d158"/>
  <circle cx="18" cy="18" r="4.5" fill="#58a6ff"/>
  <line x1="18" y1="9.5" x2="18" y2="13.5" stroke="#58a6ff" stroke-width="2" stroke-linecap="round"/>
  <line x1="9" y1="24" x2="14" y2="21" stroke="#58a6ff" stroke-width="2" stroke-linecap="round"/>
  <line x1="27" y1="24" x2="22" y2="21" stroke="#58a6ff" stroke-width="2" stroke-linecap="round"/>
  <line x1="8" y1="23" x2="16" y2="8" stroke="#30363d" stroke-width="1.5" stroke-linecap="round" opacity="0.4"/>
  <line x1="28" y1="23" x2="20" y2="8" stroke="#30363d" stroke-width="1.5" stroke-linecap="round" opacity="0.4"/>
  <line x1="9.5" y1="26" x2="26.5" y2="26" stroke="#30363d" stroke-width="1.5" stroke-linecap="round" opacity="0.4"/>
</svg>`;

function renderLanding() {
  const user = state.session?.user;
  const userIsPro = isPro(state.session);

  // Header with user info
  const headerRight = user
    ? `<div class="header-user">
        <img src="${escapeHTML(user.avatarUrl)}" alt="" class="avatar-small">
        <span>${escapeHTML(user.username)}</span>
        ${userIsPro ? '<span class="pro-badge-small">PRO</span>' : ''}
        <button class="btn btn-text btn-small" id="account-btn">Account</button>
        <button class="btn btn-text btn-small" id="signout-btn">Sign out</button>
       </div>`
    : '';

  // GitHub card content based on auth state
  let githubCardContent: string;
  if (!user) {
    // Not signed in
    githubCardContent = `
      <div class="option-icon">${GITHUB_ICON}</div>
      <h2>GitHub Integration</h2>
      <p>Analyze your repositories with persistent storage</p>
      <button class="btn btn-github" id="github-signin">${GITHUB_ICON}Sign in with GitHub</button>
    `;
  } else if (userIsPro) {
    // Pro user
    githubCardContent = `
      <div class="option-icon pro-icon">${GITHUB_ICON}</div>
      <div class="pro-badge">PRO</div>
      <h2>GitHub Integration</h2>
      <p>Persistent projects with live webhook updates</p>
      <p class="privacy-note">🔒 Code processed in-browser, only graphs saved</p>
      <button class="btn btn-primary" id="browse-repos-btn">Browse Repositories</button>
      <button class="btn btn-secondary btn-small" id="my-projects-btn" style="margin-top: 0.5rem">My Projects</button>
    `;
  } else {
    // Free user - show upgrade prompt
    githubCardContent = `
      <div class="option-icon locked-icon">${GITHUB_ICON}</div>
      <h2>GitHub Integration</h2>
      <div class="pricing-toggle">
        <button class="plan-btn active" data-plan="monthly">Monthly</button>
        <button class="plan-btn" data-plan="yearly">Yearly<span class="save-badge">17% OFF</span></button>
      </div>
      <div class="price-display">
        <span class="price-amount" id="price-amount">$9.99</span>
        <span class="price-period" id="price-period">/month</span>
      </div>
      <ul class="feature-list">
        <li>Save graphs &amp; custom notes</li>
        <li>Live webhook updates</li>
        <li>Unlimited repositories</li>
      </ul>
      <p class="privacy-note">🔒 Code stays in-browser, only graphs saved</p>
      <button class="btn btn-primary btn-glow" id="upgrade-btn">Upgrade to Pro</button>
      <p class="cancel-note">Cancel anytime</p>
    `;
  }

  const errorSection = state.error
    ? `<div class="error-message">${escapeHTML(state.error)}</div>`
    : '';

  const successSection = state.success
    ? `<div class="success-message">${escapeHTML(state.success)}</div>`
    : '';

  setHTML(app, `
    <div class="landing">
      <header class="landing-header">
        <div class="logo-container">
          <span class="app-logo">${APP_LOGO}</span>
          <h1 class="logo">webwebweb<span class="logo-tld">.dev</span></h1>
        </div>
        ${headerRight}
      </header>

      <section class="hero">
        <div class="hero-simulation" id="hero-simulation">
          <canvas id="hero-canvas"></canvas>
        </div>
        <div class="hero-content">
          <h2 class="tagline">a web of your web app, on the web</h2>
          <p class="hero-subtitle">Visualize your codebase as an interactive dependency graph</p>
        </div>
      </section>

      <section class="features">
        <div class="feature">
          <div class="feature-icon">🔍</div>
          <h3>Instant Analysis</h3>
          <p>Drop any codebase and see dependencies visualized in seconds</p>
        </div>
        <div class="feature">
          <div class="feature-icon">⏱️</div>
          <h3>Time-lapse</h3>
          <p>Watch your project evolve commit by commit</p>
        </div>
        <div class="feature">
          <div class="feature-icon">🔒</div>
          <h3>Privacy First</h3>
          <p>Code is analyzed in your browser. Pro saves graphs only, never source code</p>
        </div>
      </section>

      <main class="landing-main">
        ${successSection}
        ${errorSection}

        <h2 class="section-title">Get Started</h2>

        <div class="input-options">
          <div class="option-card ${!user ? '' : userIsPro ? 'option-card-pro' : 'option-card-locked'}" id="github-option">
            ${githubCardContent}
          </div>

          <div class="option-card" id="local-option">
            <div class="option-icon">📁</div>
            <h2>Local Folder</h2>
            <p>Analyze a folder from your computer</p>
            <span class="free-badge">Always Free</span>
            <p class="privacy-note">🔒 Code never leaves your browser</p>
            <button class="btn btn-secondary" id="local-browse">Browse Folder</button>
            <input type="file" id="folder-input" webkitdirectory style="display: none">
          </div>
        </div>

        <div class="drop-zone" id="drop-zone">
          <div class="drop-zone-content">
            <div class="drop-icon">📂</div>
            <p>Drop a folder here to analyze</p>
            <span class="drop-hint">or click to browse</span>
          </div>
        </div>
      </main>

      <footer class="landing-footer">
        <p>Built for developers who want to understand their code</p>
      </footer>
    </div>
  `);

  addStyles();
  setupLandingEvents();
  startHeroSimulation();
}

/**
 * Setup landing page event handlers
 */
function setupLandingEvents() {
  // Sign in button
  const signinBtn = document.getElementById('github-signin');
  if (signinBtn) {
    signinBtn.onclick = () => signIn();
  }

  // Account button
  const accountBtn = document.getElementById('account-btn');
  if (accountBtn) {
    accountBtn.onclick = () => showAccountModal();
  }

  // Sign out button
  const signoutBtn = document.getElementById('signout-btn');
  if (signoutBtn) {
    signoutBtn.onclick = async () => {
      await signOut();
    };
  }

  // Pricing toggle (for free users)
  let selectedPlan: 'monthly' | 'yearly' = 'monthly';
  document.querySelectorAll('.plan-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.plan-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      selectedPlan = btn.getAttribute('data-plan') as 'monthly' | 'yearly';

      const priceAmount = document.getElementById('price-amount');
      const pricePeriod = document.getElementById('price-period');
      if (priceAmount && pricePeriod) {
        if (selectedPlan === 'yearly') {
          priceAmount.textContent = '$99.99';
          pricePeriod.textContent = '/year';
        } else {
          priceAmount.textContent = '$9.99';
          pricePeriod.textContent = '/month';
        }
      }
    });
  });

  // Upgrade button (for free users)
  const upgradeBtn = document.getElementById('upgrade-btn');
  if (upgradeBtn) {
    upgradeBtn.onclick = () => handleUpgrade(selectedPlan);
  }

  // Browse repos button (for pro users)
  const browseReposBtn = document.getElementById('browse-repos-btn');
  if (browseReposBtn) {
    browseReposBtn.onclick = () => {
      state.view = 'repos';
      render();
      loadRepos();
    };
  }

  // My projects button (for pro users)
  const myProjectsBtn = document.getElementById('my-projects-btn');
  if (myProjectsBtn) {
    myProjectsBtn.onclick = () => {
      state.view = 'projects';
      render();
      loadProjects();
    };
  }

  // Local folder browsing
  const localBrowseBtn = document.getElementById('local-browse');
  const folderInput = document.getElementById('folder-input') as HTMLInputElement;

  if (localBrowseBtn && folderInput) {
    localBrowseBtn.onclick = async () => {
      state.error = null;
      state.success = null;
      if (hasFileSystemAccess()) {
        try {
          state.view = 'loading';
          render();
          const files = await openDirectoryPicker(handleProgress);
          await processLocalFiles(files, 'Local Folder');
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            state.error = (err as Error).message;
            state.view = 'landing';
            render();
          } else {
            state.view = 'landing';
            render();
          }
        }
      } else {
        folderInput.click();
      }
    };

    folderInput.onchange = async () => {
      if (folderInput.files && folderInput.files.length > 0) {
        state.view = 'loading';
        render();
        const files = await readInputFiles(folderInput.files, handleProgress);
        await processLocalFiles(files, 'Local Folder');
      }
    };
  }

  // Drop zone - also clickable to browse
  const dropZone = document.getElementById('drop-zone');
  if (dropZone && folderInput) {
    setupDropZone(dropZone, async (files) => {
      state.error = null;
      state.success = null;
      state.view = 'loading';
      render();
      await processLocalFiles(files, 'Dropped Folder');
    }, handleProgress);

    // Click to browse
    dropZone.onclick = async () => {
      if (hasFileSystemAccess()) {
        try {
          state.view = 'loading';
          render();
          const files = await openDirectoryPicker(handleProgress);
          await processLocalFiles(files, 'Local Folder');
        } catch (err) {
          if ((err as Error).name !== 'AbortError') {
            state.error = (err as Error).message;
          }
          state.view = 'landing';
          render();
        }
      } else {
        folderInput.click();
      }
    };
  }
}

// Hero simulation state
let heroAnimationId: number | null = null;

/**
 * Start the hero graph simulation - calm, settled force-directed graph
 */
function startHeroSimulation() {
  const canvas = document.getElementById('hero-canvas') as HTMLCanvasElement;
  if (!canvas) return;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Stop any existing animation
  if (heroAnimationId) {
    cancelAnimationFrame(heroAnimationId);
    heroAnimationId = null;
  }

  const container = canvas.parentElement!;
  const rect = container.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;

  let width = rect.width;
  let height = rect.height;

  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = width + 'px';
  canvas.style.height = height + 'px';
  ctx.scale(dpr, dpr);

  // Exact colors from the actual graph visualization
  const colors = [
    '#00d4ff', // component
    '#bf5af2', // hook
    '#30d158', // utility
    '#ffd60a', // api
    '#ff375f', // page
    '#64d2ff', // layout
    '#98989d', // types
    '#ff9f0a', // config
    '#5e5ce6', // context
    '#ff453a', // state
    '#0a84ff', // module
  ];

  interface SimNode {
    x: number;
    y: number;
    baseX: number;
    baseY: number;
    radius: number;
    color: string;
    phase: number;
    speed: number;
    amplitude: number;
  }

  interface SimEdge {
    source: number;
    target: number;
  }

  const nodes: SimNode[] = [];
  const edges: SimEdge[] = [];
  const nodeCount = 30;

  // Create nodes in a stable circular layout
  const centerX = width / 2;
  const centerY = height / 2;
  const baseRadius = Math.min(width, height) * 0.35;

  for (let i = 0; i < nodeCount; i++) {
    const angle = (Math.PI * 2 * i) / nodeCount;
    // Vary distance from center for more organic look
    const dist = baseRadius * (0.5 + Math.random() * 0.5);
    const x = centerX + Math.cos(angle) * dist;
    const y = centerY + Math.sin(angle) * dist;

    nodes.push({
      x,
      y,
      baseX: x,
      baseY: y,
      radius: 5 + Math.random() * 5,
      color: colors[i % colors.length],
      phase: Math.random() * Math.PI * 2,
      speed: 0.005 + Math.random() * 0.01,
      amplitude: 3 + Math.random() * 5,
    });
  }

  // Create edges - ring connections plus some cross-links
  for (let i = 0; i < nodeCount; i++) {
    // Connect to next node in ring
    edges.push({ source: i, target: (i + 1) % nodeCount });

    // Some nodes connect across
    if (i % 3 === 0) {
      const target = (i + Math.floor(nodeCount / 3)) % nodeCount;
      edges.push({ source: i, target });
    }
  }

  let time = 0;

  function draw() {
    ctx.clearRect(0, 0, width, height);
    time += 1;

    // Gentle floating motion
    nodes.forEach(node => {
      node.x = node.baseX + Math.sin(time * node.speed + node.phase) * node.amplitude;
      node.y = node.baseY + Math.cos(time * node.speed * 0.7 + node.phase) * node.amplitude;
    });

    // Draw edges
    ctx.strokeStyle = '#30363d';
    ctx.globalAlpha = 0.35;
    ctx.lineWidth = 1;
    edges.forEach(edge => {
      const source = nodes[edge.source];
      const target = nodes[edge.target];
      ctx.beginPath();
      ctx.moveTo(source.x, source.y);
      ctx.lineTo(target.x, target.y);
      ctx.stroke();
    });
    ctx.globalAlpha = 1;

    // Draw nodes
    nodes.forEach(node => {
      // Subtle glow
      const gradient = ctx.createRadialGradient(
        node.x, node.y, 0,
        node.x, node.y, node.radius * 2.5
      );
      gradient.addColorStop(0, node.color + '25');
      gradient.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius * 2.5, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      // Node circle
      ctx.beginPath();
      ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
      ctx.fillStyle = node.color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    heroAnimationId = requestAnimationFrame(draw);
  }

  draw();
}

/**
 * Show account modal
 */
function showAccountModal() {
  const user = state.session?.user;
  if (!user) return;

  const userIsPro = isPro(state.session);
  const subEnd = user.subscriptionPeriodEnd
    ? new Date(user.subscriptionPeriodEnd).toLocaleDateString()
    : null;

  const modal = document.createElement('div');
  modal.className = 'modal-overlay';

  const subscriptionContent = userIsPro ? `
    <div class="subscription-active">
      <span class="pro-badge">PRO</span>
      <p>Your subscription is active</p>
      ${subEnd ? `<p class="sub-period">Renews on ${escapeHTML(subEnd)}</p>` : ''}
    </div>
    <button class="btn btn-secondary" id="manage-billing">Manage Billing</button>
    <p class="billing-note">Change payment method, cancel, or view invoices</p>
  ` : `
    <p class="subscription-free">You're on the free plan</p>
    <button class="btn btn-primary" id="modal-upgrade">Upgrade to Pro</button>
  `;

  const modalContent = `
    <div class="modal">
      <button class="modal-close" id="modal-close">&times;</button>
      <div class="account-header">
        <img src="${escapeHTML(user.avatarUrl)}" alt="" class="avatar-large">
        <h2>${escapeHTML(user.username)}</h2>
        ${user.email ? `<p class="account-email">${escapeHTML(user.email)}</p>` : ''}
      </div>
      <div class="account-section">
        <h3>Subscription</h3>
        ${subscriptionContent}
      </div>
    </div>
  `;

  setHTML(modal, modalContent);
  document.body.appendChild(modal);

  // Close modal
  modal.querySelector('#modal-close')!.addEventListener('click', () => modal.remove());
  modal.addEventListener('click', (e) => {
    if (e.target === modal) modal.remove();
  });

  // Manage billing
  const billingBtn = modal.querySelector('#manage-billing');
  if (billingBtn) {
    billingBtn.addEventListener('click', async () => {
      try {
        const res = await fetch('/api/stripe/portal', {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const { url } = await res.json();
          window.location.href = url;
        } else {
          const data = await res.json();
          alert(data.error || 'Failed to open billing portal');
        }
      } catch {
        alert('Failed to open billing portal');
      }
    });
  }

  // Upgrade from modal
  const upgradeBtn = modal.querySelector('#modal-upgrade');
  if (upgradeBtn) {
    upgradeBtn.addEventListener('click', () => {
      modal.remove();
      handleUpgrade('monthly');
    });
  }
}

/**
 * Handle upgrade to Pro
 */
async function handleUpgrade(plan: 'monthly' | 'yearly' = 'monthly') {
  try {
    const res = await fetch('/api/stripe/checkout', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plan }),
    });

    if (!res.ok) {
      const data = await res.json();
      state.error = data.error || 'Failed to start checkout';
      render();
      return;
    }

    const { url } = await res.json();
    window.location.href = url;
  } catch (err) {
    state.error = 'Failed to start checkout. Please try again.';
    render();
  }
}

/**
 * Load saved projects for pro users
 */
async function loadProjects() {
  try {
    const res = await fetch('/api/projects', {
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error('Failed to load projects');
    }

    const { projects } = await res.json();
    state.savedProjects = projects;
    render();
  } catch (err) {
    state.error = (err as Error).message;
    render();
  }
}

/**
 * Render saved projects dashboard
 */
function renderProjects() {
  const user = state.session?.user;

  const baseUrl = window.location.origin;
  const projectCards = state.savedProjects.length === 0
    ? `<div class="empty-projects">
        <div class="empty-icon">&#128193;</div>
        <h3>No saved projects yet</h3>
        <p>Save GitHub repositories to access them anytime with persistent visualizations</p>
        <button class="btn btn-primary" id="empty-add-btn">+ Add Your First Project</button>
      </div>`
    : state.savedProjects.map(project => `
        <div class="project-card" data-project-id="${project.id}">
          <div class="project-card-header">
            <div class="project-icon">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9zm10.5-1V9h-8c-.356 0-.694.074-1 .208V2.5a1 1 0 011-1h8z"/>
              </svg>
            </div>
            <div class="project-info">
              <h3>${escapeHTML(project.fullName)}</h3>
              <span class="project-branch">${escapeHTML(project.repo)}</span>
            </div>
            <div class="project-badges">
              ${project.hasWebhook ? '<span class="live-badge">Live</span>' : ''}
              ${project.isPublic ? '<span class="shared-badge">Shared</span>' : ''}
            </div>
          </div>

          ${project.stats ? `
            <div class="project-stats-grid">
              <div class="stat-box">
                <span class="stat-number">${project.stats.nodeCount}</span>
                <span class="stat-label">Files</span>
              </div>
              <div class="stat-box">
                <span class="stat-number">${project.stats.edgeCount}</span>
                <span class="stat-label">Connections</span>
              </div>
              <div class="stat-box">
                <span class="stat-number">${(project.stats.totalLines / 1000).toFixed(1)}k</span>
                <span class="stat-label">Lines</span>
              </div>
            </div>
          ` : '<div class="project-not-analyzed">Not analyzed yet</div>'}

          <div class="project-footer">
            <span class="project-updated">
              ${project.lastAnalyzedAt
                ? `Updated ${formatRelativeTime(project.lastAnalyzedAt)}`
                : 'Never analyzed'}
            </span>
            ${project.status === 'pending' ? '<span class="needs-update">Needs update</span>' : ''}
          </div>

          ${project.isPublic && project.shareToken ? `
            <div class="share-link-row">
              <input type="text" class="share-link-input" value="${baseUrl}/s/${project.shareToken}" readonly>
              <button class="btn btn-small copy-link-btn" title="Copy link">Copy</button>
            </div>
          ` : ''}

          <div class="project-actions">
            <button class="btn btn-primary btn-small view-project-btn">Open</button>
            <button class="btn btn-small share-project-btn" title="${project.isPublic ? 'Stop sharing' : 'Share publicly'}">
              ${project.isPublic ? '&#128275; Unshare' : '&#128279; Share'}
            </button>
            <button class="btn btn-small settings-project-btn" title="Project settings">&#9881;</button>
            <button class="btn btn-text btn-small delete-project-btn" title="Delete project">&#128465;</button>
          </div>
        </div>
      `).join('');

  setHTML(app, `
    <div class="projects-page">
      <header class="page-header">
        <button class="btn btn-text" id="back-btn">Back</button>
        <h1>My Projects</h1>
        <div class="header-user">
          <img src="${escapeHTML(user?.avatarUrl || '')}" alt="" class="avatar-small">
          <span>${escapeHTML(user?.username || '')}</span>
        </div>
      </header>

      <div class="projects-actions">
        <button class="btn btn-primary" id="add-project-btn">+ Add Project</button>
      </div>

      <div class="projects-grid">
        ${projectCards}
      </div>

      <!-- Settings Modal -->
      <div class="modal-overlay" id="settings-modal" style="display: none;">
        <div class="modal settings-modal">
          <div class="modal-header">
            <h2>Project Settings</h2>
            <button class="modal-close" id="close-settings">&times;</button>
          </div>
          <div class="modal-body">
            <input type="hidden" id="settings-project-id">

            <div class="form-group">
              <label for="project-description">Description</label>
              <textarea id="project-description" rows="3" placeholder="Add notes about this project..."></textarea>
            </div>

            <div class="form-section">
              <h3>Default View Settings</h3>
              <p class="form-hint">These settings will be applied when you open this project</p>

              <div class="form-group">
                <label for="default-view">Default View</label>
                <select id="default-view">
                  <option value="graph">Graph</option>
                  <option value="tree">Tree</option>
                  <option value="metrics">Metrics</option>
                </select>
              </div>

              <div class="form-group">
                <label for="default-clustering">Node Grouping</label>
                <select id="default-clustering">
                  <option value="none">None</option>
                  <option value="type">By Type</option>
                  <option value="folder">By Folder</option>
                </select>
              </div>

              <div class="form-group">
                <label for="default-size">Node Size</label>
                <select id="default-size">
                  <option value="lines">By Lines of Code</option>
                  <option value="connections">By Connections</option>
                </select>
              </div>

              <div class="form-row">
                <label class="checkbox-label">
                  <input type="checkbox" id="show-labels">
                  <span>Show labels by default</span>
                </label>
              </div>

              <div class="form-row">
                <label class="checkbox-label">
                  <input type="checkbox" id="hide-orphans">
                  <span>Hide orphan files by default</span>
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-text" id="cancel-settings">Cancel</button>
            <button class="btn btn-primary" id="save-settings">Save Settings</button>
          </div>
        </div>
      </div>
    </div>
  `);

  addStyles();
  addSettingsModalStyles();

  // Event handlers
  document.getElementById('back-btn')!.onclick = () => {
    state.view = 'landing';
    render();
  };

  const addProjectHandler = () => {
    state.view = 'repos';
    render();
    loadRepos();
  };

  const addProjectBtn = document.getElementById('add-project-btn');
  if (addProjectBtn) addProjectBtn.onclick = addProjectHandler;

  const emptyAddBtn = document.getElementById('empty-add-btn');
  if (emptyAddBtn) emptyAddBtn.onclick = addProjectHandler;

  // View project buttons
  document.querySelectorAll('.view-project-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const card = (e.target as HTMLElement).closest('.project-card');
      const projectId = card?.getAttribute('data-project-id');
      if (projectId) {
        await viewProject(projectId);
      }
    });
  });

  // Delete project buttons
  document.querySelectorAll('.delete-project-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const card = (e.target as HTMLElement).closest('.project-card');
      const projectId = card?.getAttribute('data-project-id');
      if (projectId && confirm('Delete this project?')) {
        await deleteProject(projectId);
      }
    });
  });

  // Share project buttons
  document.querySelectorAll('.share-project-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const card = (e.target as HTMLElement).closest('.project-card');
      const projectId = card?.getAttribute('data-project-id');
      if (!projectId) return;

      const project = state.savedProjects.find(p => p.id === projectId);
      if (!project) return;

      const button = e.target as HTMLButtonElement;
      button.disabled = true;
      button.textContent = project.isPublic ? 'Unsharing...' : 'Sharing...';

      try {
        const res = await fetch(`/api/projects/${projectId}/share`, {
          method: project.isPublic ? 'DELETE' : 'POST',
          credentials: 'include',
        });

        if (!res.ok) throw new Error('Failed to update sharing');

        const data = await res.json();

        // Update local state
        project.isPublic = data.isPublic;
        project.shareToken = data.shareToken || null;

        render();
      } catch (err) {
        state.error = (err as Error).message;
        button.disabled = false;
        button.textContent = project.isPublic ? 'Unshare' : 'Share';
      }
    });
  });

  // Copy link buttons
  document.querySelectorAll('.copy-link-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      const row = (e.target as HTMLElement).closest('.share-link-row');
      const input = row?.querySelector('.share-link-input') as HTMLInputElement;
      if (!input) return;

      try {
        await navigator.clipboard.writeText(input.value);
        const button = e.target as HTMLButtonElement;
        button.textContent = 'Copied!';
        setTimeout(() => {
          button.textContent = 'Copy';
        }, 2000);
      } catch {
        // Fallback for older browsers
        input.select();
        document.execCommand('copy');
      }
    });
  });

  // Settings modal handlers
  const settingsModal = document.getElementById('settings-modal')!;
  const closeSettings = document.getElementById('close-settings')!;
  const cancelSettings = document.getElementById('cancel-settings')!;
  const saveSettings = document.getElementById('save-settings')!;

  const closeSettingsModal = () => {
    settingsModal.style.display = 'none';
  };

  closeSettings.onclick = closeSettingsModal;
  cancelSettings.onclick = closeSettingsModal;
  settingsModal.onclick = (e) => {
    if (e.target === settingsModal) closeSettingsModal();
  };

  // Settings project buttons
  document.querySelectorAll('.settings-project-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const card = (e.target as HTMLElement).closest('.project-card');
      const projectId = card?.getAttribute('data-project-id');
      if (!projectId) return;

      const project = state.savedProjects.find(p => p.id === projectId);
      if (!project) return;

      // Populate modal with current settings
      (document.getElementById('settings-project-id') as HTMLInputElement).value = projectId;
      (document.getElementById('project-description') as HTMLTextAreaElement).value = project.description || '';
      (document.getElementById('default-view') as HTMLSelectElement).value = project.settings.defaultView;
      (document.getElementById('default-clustering') as HTMLSelectElement).value = project.settings.defaultClustering;
      (document.getElementById('default-size') as HTMLSelectElement).value = project.settings.sizeMode;
      (document.getElementById('show-labels') as HTMLInputElement).checked = project.settings.showLabels;
      (document.getElementById('hide-orphans') as HTMLInputElement).checked = project.settings.hideOrphans;

      // Show modal
      settingsModal.style.display = 'flex';
    });
  });

  // Save settings handler
  saveSettings.onclick = async () => {
    const projectId = (document.getElementById('settings-project-id') as HTMLInputElement).value;
    const description = (document.getElementById('project-description') as HTMLTextAreaElement).value;
    const settings = {
      defaultView: (document.getElementById('default-view') as HTMLSelectElement).value,
      defaultClustering: (document.getElementById('default-clustering') as HTMLSelectElement).value,
      sizeMode: (document.getElementById('default-size') as HTMLSelectElement).value,
      showLabels: (document.getElementById('show-labels') as HTMLInputElement).checked,
      hideOrphans: (document.getElementById('hide-orphans') as HTMLInputElement).checked,
    };

    saveSettings.textContent = 'Saving...';
    (saveSettings as HTMLButtonElement).disabled = true;

    try {
      const res = await fetch(`/api/projects/${projectId}/settings`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description, settings }),
      });

      if (!res.ok) throw new Error('Failed to save settings');

      const data = await res.json();

      // Update local state
      const project = state.savedProjects.find(p => p.id === projectId);
      if (project) {
        project.description = data.description;
        project.settings = data.settings;
      }

      closeSettingsModal();
      state.success = 'Settings saved!';
      render();
    } catch (err) {
      state.error = (err as Error).message;
      saveSettings.textContent = 'Save Settings';
      (saveSettings as HTMLButtonElement).disabled = false;
    }
  };
}

/**
 * View a saved project
 */
async function viewProject(projectId: string) {
  state.view = 'loading';
  state.progress = { phase: 'Loading', current: 0, total: 1, message: 'Loading saved project...' };
  render();

  try {
    const res = await fetch(`/api/projects/${projectId}`, {
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error('Failed to load project');
    }

    const { project, snapshot } = await res.json();

    if (!snapshot) {
      // No snapshot, need to analyze
      state.error = 'Project needs to be analyzed first';
      state.view = 'projects';
      render();
      return;
    }

    state.currentProjectId = projectId;
    state.selectedRepo = {
      id: 0,
      name: project.repo,
      full_name: project.fullName,
      private: project.isPrivate,
      description: null,
      html_url: `https://github.com/${project.fullName}`,
      clone_url: `https://github.com/${project.fullName}.git`,
      default_branch: project.defaultBranch,
      updated_at: '',
      pushed_at: '',
      stargazers_count: 0,
      language: null,
      owner: { login: project.owner, avatar_url: '' }
    };
    state.graph = snapshot.graphData;

    if (snapshot.timelapseData) {
      state.timelapse = {
        ...state.timelapse,
        ...snapshot.timelapseData,
        isReady: true,
      };
    }

    // Store saved node positions to apply after graph initialization
    state.savedNodePositions = snapshot.nodePositions || null;

    // Load saved annotations
    state.nodeAnnotations = snapshot.nodeAnnotations || {};

    // Store project settings to apply after graph initialization
    state.currentProjectSettings = project.settings || null;

    state.view = 'visualization';
    render();
  } catch (err) {
    state.error = (err as Error).message;
    state.view = 'projects';
    render();
  }
}

/**
 * Delete a saved project
 */
async function deleteProject(projectId: string) {
  try {
    const res = await fetch(`/api/projects/${projectId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!res.ok) {
      throw new Error('Failed to delete project');
    }

    state.savedProjects = state.savedProjects.filter(p => p.id !== projectId);
    render();
  } catch (err) {
    state.error = (err as Error).message;
    render();
  }
}

/**
 * Load a shared project by token (public view)
 */
async function loadSharedProject(token: string) {
  // Hide loading spinner first
  const loading = document.getElementById('loading');
  if (loading) loading.style.display = 'none';

  state.view = 'loading';
  state.progress = { phase: 'Loading', current: 0, total: 1, message: 'Loading shared project...' };
  render();

  try {
    const res = await fetch(`/api/share/${token}`);

    if (!res.ok) {
      const data = await res.json();
      throw new Error(data.error || 'Failed to load shared project');
    }

    const { project, snapshot } = await res.json();

    // Set up state for shared view
    state.isViewingShared = true;
    state.sharedProjectInfo = {
      fullName: project.fullName,
      ownerUsername: project.ownerUsername,
      ownerAvatar: project.ownerAvatar,
    };
    state.graph = snapshot.graphData;
    state.savedNodePositions = snapshot.nodePositions || null;
    state.nodeAnnotations = {};  // Don't load annotations for shared view

    state.view = 'shared';
    render();
  } catch (err) {
    state.error = (err as Error).message;
    state.view = 'landing';
    state.isViewingShared = false;
    state.sharedProjectInfo = null;
    // Redirect to home
    window.history.replaceState({}, '', '/');
    render();
  }
}

/**
 * Save current analysis as a project (pro users only)
 */
async function saveProject() {
  if (!state.selectedRepo || !state.graph) {
    state.error = 'No repository to save';
    render();
    return;
  }

  const saveBtn = document.getElementById('save-project-btn');
  if (saveBtn) {
    saveBtn.textContent = 'Saving...';
    (saveBtn as HTMLButtonElement).disabled = true;
  }

  try {
    // Step 1: Create the project
    const createRes = await fetch('/api/projects/create', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ repo: state.selectedRepo }),
    });

    if (!createRes.ok) {
      const data = await createRes.json();
      throw new Error(data.error || 'Failed to create project');
    }

    const { projectId } = await createRes.json();

    // Step 2: Save the snapshot (including current node positions)
    const latestCommit = state.timelapse.sortedCommits[state.timelapse.sortedCommits.length - 1];
    const nodePositions = getNodePositions();
    const snapshotRes = await fetch(`/api/projects/${projectId}/snapshot`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        commitOid: latestCommit?.oid || 'HEAD',
        commitMessage: latestCommit?.message || null,
        commitDate: latestCommit?.date || null,
        graphData: state.graph,
        timelapseData: state.timelapse.isReady ? {
          sortedCommits: state.timelapse.sortedCommits,
          filesByCommit: Object.fromEntries(
            Array.from(state.timelapse.filesByCommit.entries()).map(([k, v]) => [k, Array.from(v)])
          ),
          changesByCommit: Object.fromEntries(state.timelapse.changesByCommit.entries()),
        } : null,
        nodePositions: nodePositions,
        nodeAnnotations: Object.keys(state.nodeAnnotations).length > 0 ? state.nodeAnnotations : null,
        nodeCount: state.graph.nodes.length,
        edgeCount: state.graph.edges.length,
        totalLines: state.graph.metrics.totalLines,
      }),
    });

    if (!snapshotRes.ok) {
      throw new Error('Failed to save snapshot');
    }

    // Success! Update state
    state.currentProjectId = projectId;
    state.success = 'Project saved successfully!';
    render();

  } catch (err) {
    state.error = (err as Error).message;
    if (saveBtn) {
      saveBtn.textContent = '💾 Save Project';
      (saveBtn as HTMLButtonElement).disabled = false;
    }
    render();
  }
}

/**
 * Format relative time
 */
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}



/**
 * Render repository selector
 */
function renderRepoSelector() {
  const repoListContent = state.repos.length === 0
    ? '<div class="loading-repos">Loading repositories...</div>'
    : state.repos.map(repo => `
        <div class="repo-card" data-repo="${escapeHTML(repo.full_name)}">
          <div class="repo-info">
            <h3>${escapeHTML(repo.name)}</h3>
            <p>${escapeHTML(repo.description || 'No description')}</p>
            <div class="repo-meta">
              ${repo.language ? `<span class="language">${escapeHTML(repo.language)}</span>` : ''}
              <span class="stars">* ${repo.stargazers_count}</span>
              ${repo.private ? '<span class="private">Private</span>' : ''}
            </div>
          </div>
          <button class="btn btn-primary">Analyze</button>
        </div>
      `).join('');

  const errorSection = state.error
    ? `<div class="error-message">${escapeHTML(state.error)}</div>`
    : '';

  const user = state.session?.user;

  setHTML(app, `
    <div class="repo-selector">
      <header class="selector-header">
        <button class="btn btn-text" id="back-btn">Back</button>
        <h1>Select Repository</h1>
        <div class="user-info">
          <img src="${escapeHTML(user?.avatarUrl || '')}" alt="" class="avatar">
          <span>${escapeHTML(user?.username || '')}</span>
          <button class="btn btn-text" id="signout-btn">Sign out</button>
        </div>
      </header>

      <div class="search-bar">
        <input type="text" id="repo-search" placeholder="Search repositories..." />
      </div>

      <div class="repo-list" id="repo-list">
        ${repoListContent}
      </div>

      ${errorSection}
    </div>
  `);

  addStyles();

  document.getElementById('back-btn')!.onclick = () => {
    state.view = 'landing';
    render();
  };

  document.getElementById('signout-btn')!.onclick = async () => {
    await signOut();
  };

  const repoCards = document.querySelectorAll('.repo-card');
  repoCards.forEach(card => {
    const btn = card.querySelector('button');
    if (btn) {
      btn.onclick = () => {
        const fullName = card.getAttribute('data-repo')!;
        const repo = state.repos.find(r => r.full_name === fullName);
        if (repo) {
          analyzeGitHubRepo(repo);
        }
      };
    }
  });

  const searchInput = document.getElementById('repo-search') as HTMLInputElement;
  if (searchInput) {
    searchInput.oninput = () => {
      const query = searchInput.value.toLowerCase();
      repoCards.forEach(card => {
        const name = card.querySelector('h3')?.textContent?.toLowerCase() || '';
        const desc = card.querySelector('p')?.textContent?.toLowerCase() || '';
        (card as HTMLElement).style.display =
          name.includes(query) || desc.includes(query) ? 'flex' : 'none';
      });
    };
  }
}

/**
 * Load user repositories
 */
async function loadRepos() {
  try {
    const token = await getAccessToken();
    if (!token) {
      state.error = 'Not authenticated';
      state.view = 'landing';
      render();
      return;
    }
    state.repos = await getUserRepos(token, { perPage: 100 });
    render();
  } catch (err) {
    state.error = (err as Error).message;
    render();
  }
}

/**
 * Analyze a GitHub repository
 * Uses progressive loading: show graph fast, build timelapse in background
 */
async function analyzeGitHubRepo(repo: GitHubRepo) {
  state.selectedRepo = repo;
  state.view = 'loading';
  state.error = null;
  render();

  console.log(`[www] Starting analysis of ${repo.full_name}`);

  try {
    const [owner, repoName] = repo.full_name.split('/');

    // Force fresh clone by clearing cached data
    console.log(`[www] Clearing cache for fresh clone...`);
    await resetFS();

    // PHASE 1: Fast shallow clone to show graph quickly
    console.log(`[www] Quick clone (shallow) for immediate display...`);
    const token = await getAccessToken() || '';
    const dir = await cloneRepo(owner, repoName, token, {
      singleBranch: true,
      depth: 1,  // Just HEAD for fast initial load
      onProgress: handleProgress
    });

    console.log(`[www] Reading files...`);
    const files = await readAllRepoFiles(dir, shouldIncludeFile, handleProgress);
    console.log(`[www] Found ${files.size} code files`);

    console.log(`[www] Scanning imports/exports...`);
    const scanResult = scanFileMap(files, handleProgress);

    console.log(`[www] Building dependency graph...`);
    state.graph = buildDependencyGraph(scanResult);

    // Show graph immediately!
    console.log(`[www] Rendering graph (timelapse loading in background)...`);
    state.timelapse.isPrecomputing = true;
    state.view = 'visualization';
    render();

    // PHASE 2: Fetch full history in background for timelapse
    buildTimelapseInBackground(dir, owner, repoName, files);

  } catch (err) {
    console.error(`[www] Error:`, err);
    const errorMsg = (err as Error).message || 'An unknown error occurred';

    // Provide better error messages for common issues
    if (errorMsg.includes('401') || errorMsg.includes('Authentication')) {
      state.error = `Authentication failed. Private repos aren't fully supported yet due to CORS proxy limitations. Try a public repo, or use the local folder option.`;
    } else if (errorMsg.includes('404')) {
      state.error = `Repository not found. Make sure the repo exists and you have access.`;
    } else if (errorMsg.includes('Buffer')) {
      state.error = `Browser compatibility issue. Try refreshing the page.`;
    } else {
      state.error = errorMsg;
    }

    state.view = 'repos';
    render();
  }
}

/**
 * Build timelapse data in background after graph is displayed
 * Uses Gource-style approach: fetch A/M/D changes from GitHub API
 */
async function buildTimelapseInBackground(dir: string, owner: string, repoName: string, currentFiles: Map<string, string>) {
  try {
    console.log(`[www] Background: Building timelapse using Gource-style GitHub API...`);

    // Get graph node IDs for filtering
    const graphNodeIds = new Set(state.graph!.nodes.map(n => n.id));

    // Use GitHub API to fetch commit changes (Gource-style: A/M/D per commit)
    // This is much more reliable than trying to read git tree objects
    const bgToken = await getAccessToken() || '';
    const timelapseData = await buildTimelapseFromGitHub(
      owner,
      repoName,
      bgToken,
      graphNodeIds,
      {
        maxCommits: 1000,  // Full history for detailed timelapse
        onProgress: (p) => {
          const loadingText = document.querySelector('.timelapse-loading span');
          if (loadingText) {
            loadingText.textContent = p.message || `Loading history: ${p.current}/${p.total}`;
          }
        }
      }
    );

    if (timelapseData.sortedCommits.length > 0) {
      state.timelapse.sortedCommits = timelapseData.sortedCommits;
      state.timelapse.filesByCommit = timelapseData.filesByCommit;
      state.timelapse.changesByCommit = timelapseData.changesByCommit;
      state.timelapse.isReady = true;
      state.timelapse.isPrecomputing = false;
      state.currentCommitIndex = timelapseData.sortedCommits.length - 1;

      // Also store commits for display
      state.commits = timelapseData.sortedCommits;

      console.log(`[www] Background: Gource-style timelapse ready with ${timelapseData.sortedCommits.length} commits`);

      // Update UI to show timelapse controls
      updateTimelapseUI();
    } else {
      console.log(`[www] Background: No commits found`);
      state.timelapse.isPrecomputing = false;
      updateTimelapseUI();
    }
  } catch (err) {
    console.error(`[www] Background timelapse error:`, err);
    state.timelapse.isPrecomputing = false;
    state.timelapse.isReady = false;
    updateTimelapseUI();
  }
}

/**
 * Update timelapse UI after background loading completes
 */
function updateTimelapseUI() {
  const timelapseSection = document.querySelector('.timelapse-section');
  if (!timelapseSection) return;

  if (state.timelapse.isReady && state.timelapse.sortedCommits.length > 0) {
    // Show timelapse controls
    const sortedCommitsCount = state.timelapse.sortedCommits.length;
    timelapseSection.innerHTML = `
      <h3>Time-lapse</h3>
      <div class="timelapse-controls">
        <div class="timelapse-buttons">
          <button class="btn btn-small" id="play-btn" title="Play from oldest commit">▶ Play</button>
          <button class="btn btn-small" id="reset-timelapse" title="Reset to current state">⟲ Reset</button>
        </div>
        <input type="range" id="commit-slider" min="0" max="${sortedCommitsCount - 1}" value="${state.currentCommitIndex}">
        <div class="commit-info" id="commit-info"></div>
      </div>
    `;

    // Re-attach event handlers
    const playBtn = document.getElementById('play-btn');
    const resetBtn = document.getElementById('reset-timelapse');
    const slider = document.getElementById('commit-slider') as HTMLInputElement;

    if (playBtn && slider) {
      playBtn.onclick = () => {
        if (!state.timelapse.isReady) return;
        state.isPlaying = !state.isPlaying;
        playBtn.textContent = state.isPlaying ? '⏸ Pause' : '▶ Play';
        if (state.isPlaying) {
          if (state.currentCommitIndex >= state.timelapse.sortedCommits.length - 1) {
            state.currentCommitIndex = 0;
            slider.value = '0';
          }
          updateTimelapseView(state.currentCommitIndex);
          playTimelapse(slider);
        } else if (state.timelapse.playbackTimer) {
          clearTimeout(state.timelapse.playbackTimer);
          state.timelapse.playbackTimer = null;
        }
      };

      slider.oninput = () => {
        if (state.isPlaying) {
          state.isPlaying = false;
          playBtn.textContent = '▶ Play';
          if (state.timelapse.playbackTimer) {
            clearTimeout(state.timelapse.playbackTimer);
            state.timelapse.playbackTimer = null;
          }
        }
        state.currentCommitIndex = parseInt(slider.value);
        updateTimelapseView(state.currentCommitIndex);
      };
    }

    if (resetBtn && slider) {
      resetBtn.onclick = () => {
        if (state.isPlaying) {
          state.isPlaying = false;
          if (playBtn) playBtn.textContent = '▶ Play';
          if (state.timelapse.playbackTimer) {
            clearTimeout(state.timelapse.playbackTimer);
            state.timelapse.playbackTimer = null;
          }
        }
        state.currentCommitIndex = state.timelapse.sortedCommits.length - 1;
        slider.value = String(state.currentCommitIndex);
        updateTimelapseView(state.currentCommitIndex);
      };
    }

    // Update commit info
    updateCommitInfo();
  } else if (state.timelapse.isPrecomputing) {
    timelapseSection.innerHTML = `
      <h3>Time-lapse</h3>
      <div class="timelapse-loading">
        <div class="timelapse-spinner"></div>
        <span>Loading history...</span>
      </div>
    `;
  } else {
    timelapseSection.innerHTML = `
      <h3>Time-lapse</h3>
      <div class="timelapse-unavailable">
        Not available (no git history)
      </div>
    `;
  }
}

/**
 * Process local files
 */
async function processLocalFiles(files: Map<string, string>, name: string) {
  try {
    const scanResult = scanFileMap(files, handleProgress);
    state.graph = buildDependencyGraph(scanResult);
    state.commits = [];
    state.fileHistories = new Map();
    state.view = 'visualization';
    render();
  } catch (err) {
    state.error = (err as Error).message;
    state.view = 'landing';
    render();
  }
}

/**
 * Handle progress updates
 */
function handleProgress(progress: { phase: string; current: number; total: number; message?: string }) {
  state.progress = progress;
  if (state.view === 'loading') {
    const progressBar = document.getElementById('progress-bar') as HTMLElement;
    const progressText = document.getElementById('progress-text');

    if (progressBar && progressText) {
      const percent = Math.round((progress.current / progress.total) * 100);
      progressBar.style.width = `${percent}%`;
      progressText.textContent = progress.message || `${progress.phase}: ${percent}%`;
    }
  }
}

/**
 * Render loading screen
 */
function renderLoading() {
  const repoName = state.selectedRepo?.full_name || 'your code';
  const phase = state.progress?.phase || 'Preparing';
  const message = state.progress?.message || 'Initializing...';

  setHTML(app, `
    <div class="loading-screen">
      <div class="loading-content">
        <div class="loading-logo">${APP_LOGO}</div>
        <h2>Analyzing ${escapeHTML(repoName)}</h2>
        <div class="loading-graph">
          <div class="loading-node n1"></div>
          <div class="loading-node n2"></div>
          <div class="loading-node n3"></div>
          <div class="loading-node n4"></div>
          <svg class="loading-edges" viewBox="0 0 100 100">
            <line x1="50" y1="20" x2="20" y2="50" stroke="var(--accent-blue)" stroke-width="2" opacity="0.3"/>
            <line x1="50" y1="20" x2="80" y2="50" stroke="var(--accent-blue)" stroke-width="2" opacity="0.3"/>
            <line x1="20" y1="50" x2="50" y2="80" stroke="var(--accent-blue)" stroke-width="2" opacity="0.3"/>
            <line x1="80" y1="50" x2="50" y2="80" stroke="var(--accent-blue)" stroke-width="2" opacity="0.3"/>
          </svg>
        </div>
        <div class="progress-container">
          <div class="progress-bar" id="progress-bar"></div>
        </div>
        <p class="progress-phase">${escapeHTML(phase)}</p>
        <p class="progress-text" id="progress-text">${escapeHTML(message)}</p>
      </div>
    </div>
  `);

  addStyles();
  addLoadingStyles();
}

/**
 * Add loading-specific styles
 */
function addLoadingStyles() {
  if (document.getElementById('loading-styles')) return;

  const style = document.createElement('style');
  style.id = 'loading-styles';
  style.textContent = `
    .loading-logo { margin-bottom: 1rem; }
    .loading-logo svg { width: 48px; height: 48px; }
    .loading-graph {
      position: relative;
      width: 100px;
      height: 100px;
      margin: 1.5rem auto;
    }
    .loading-edges {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
    }
    .loading-node {
      position: absolute;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      animation: pulse-node 1.5s ease-in-out infinite;
    }
    .loading-node.n1 { top: 10%; left: 50%; transform: translateX(-50%); background: #00d4ff; animation-delay: 0s; }
    .loading-node.n2 { top: 45%; left: 10%; background: #bf5af2; animation-delay: 0.2s; }
    .loading-node.n3 { top: 45%; right: 10%; background: #30d158; animation-delay: 0.4s; }
    .loading-node.n4 { bottom: 10%; left: 50%; transform: translateX(-50%); background: #58a6ff; animation-delay: 0.6s; }
    @keyframes pulse-node {
      0%, 100% { transform: translateX(-50%) scale(1); opacity: 1; }
      50% { transform: translateX(-50%) scale(1.3); opacity: 0.7; }
    }
    .loading-node.n2, .loading-node.n3 {
      animation-name: pulse-node-side;
    }
    @keyframes pulse-node-side {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.3); opacity: 0.7; }
    }
    .progress-phase {
      color: var(--accent-blue);
      font-size: 0.9rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }
    .progress-text {
      color: var(--text-secondary);
      font-size: 0.85rem;
    }
  `;

  document.head.appendChild(style);
}

// View state for visualization
let currentView: 'graph' | 'tree' | 'metrics' = 'graph';
let activeFilters = { types: new Set<string>(), dirs: new Set<string>() };

/**
 * Render visualization
 */
function renderVisualization() {
  if (!state.graph) {
    state.view = 'landing';
    render();
    return;
  }

  const { metrics, circularDeps, externalPackages, clusters } = state.graph;
  const repoName = state.selectedRepo?.name || 'Local Folder';

  // Build type filter chips
  const typeChips = Object.entries(metrics.filesByGroup)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .map(([type, count]) => {
      const color = groupColors[type] || groupColors.other;
      return `<div class="filter-chip${activeFilters.types.has(type) ? ' active' : ''}" data-type="${escapeHTML(type)}">
        <span class="chip-color" style="background:${color}"></span>
        ${escapeHTML(type)}
        <span class="chip-count">(${count})</span>
      </div>`;
    }).join('');

  // Build directory filter chips
  const dirChips = clusters.slice(0, 8).map(cluster => {
    const name = cluster.name || 'root';
    return `<div class="filter-chip${activeFilters.dirs.has(cluster.name) ? ' active' : ''}" data-dir="${escapeHTML(cluster.name)}">
      ${escapeHTML(name)}
      <span class="chip-count">(${cluster.files.length})</span>
    </div>`;
  }).join('');

  // Build legend
  const usedGroups = new Set(state.graph.nodes.map(n => n.group));
  const legendItems = Array.from(usedGroups).sort().map(group => {
    const color = groupColors[group] || groupColors.other;
    return `<div class="legend-item">
      <div class="legend-color" style="background:${color}"></div>
      <span>${escapeHTML(group)}</span>
    </div>`;
  }).join('');

  // Quick stats
  const quickStats = `
    <div class="stat-row"><span>Avg imports/file:</span><span>${metrics.avgImportsPerFile}</span></div>
    <div class="stat-row"><span>Avg lines/file:</span><span>${metrics.avgLinesPerFile}</span></div>
    <div class="stat-row"><span>External packages:</span><span>${externalPackages.length}</span></div>
    <div class="stat-row"><span>Circular deps:</span><span class="${circularDeps.length > 0 ? 'warning' : 'success'}">${circularDeps.length}</span></div>
  `;

  // Time-lapse section (sorted commits oldest first)
  const sortedCommitsCount = state.timelapse.sortedCommits.length;
  const timelapseReady = state.timelapse.isReady;
  const timelapsePrecomputing = state.timelapse.isPrecomputing;

  let timelapseSection = '';
  // Show timelapse section if precomputing OR if we have commits
  if (timelapsePrecomputing || state.commits.length > 0 || timelapseReady) {
    if (timelapsePrecomputing) {
      timelapseSection = `
        <div class="sidebar-section timelapse-section">
          <h3>Time-lapse</h3>
          <div class="timelapse-loading">
            <div class="timelapse-spinner"></div>
            <span>Preparing timelapse...</span>
          </div>
        </div>
      `;
    } else if (timelapseReady && sortedCommitsCount > 0) {
      const firstCommitFiles = state.timelapse.filesByCommit.get(state.timelapse.sortedCommits[0]?.oid)?.size || 0;
      const lastCommitFiles = state.timelapse.filesByCommit.get(state.timelapse.sortedCommits[sortedCommitsCount - 1]?.oid)?.size || 0;
      timelapseSection = `
        <div class="sidebar-section timelapse-section">
          <h3>Time-lapse</h3>
          <div class="timelapse-controls">
            <div class="timelapse-buttons">
              <button class="btn btn-small" id="play-btn" title="Play from oldest commit">▶ Play</button>
              <button class="btn btn-small btn-secondary" id="reset-timelapse" title="Reset to latest">⟳</button>
            </div>
            <input type="range" id="commit-slider" min="0" max="${sortedCommitsCount - 1}" value="${state.currentCommitIndex}">
            <div id="commit-info" class="commit-info">${sortedCommitsCount} commits (${firstCommitFiles} → ${lastCommitFiles} files)</div>
          </div>
        </div>
      `;
    } else {
      timelapseSection = `
        <div class="sidebar-section timelapse-section">
          <h3>Time-lapse</h3>
          <div class="timelapse-unavailable">
            <span>Timelapse unavailable</span>
          </div>
        </div>
      `;
    }
  }

  // Show save button for pro users viewing a GitHub repo (not local folder, not already saved)
  const canSave = isPro(state.session) && state.selectedRepo && !state.currentProjectId;
  const isSaved = !!state.currentProjectId;
  const saveButton = canSave
    ? `<button class="btn btn-primary btn-small" id="save-project-btn">💾 Save Project</button>`
    : isSaved
      ? `<span class="saved-badge">✓ Saved</span>`
      : '';

  setHTML(app, `
    <div class="visualization full">
      <header class="viz-header">
        <button class="btn btn-text" id="viz-back">&larr; Back</button>
        <h1>${escapeHTML(repoName)}</h1>
        <div class="search-container">
          <input type="text" id="search-input" class="search-input" placeholder="Search files, exports...">
        </div>
        <div class="header-stats">
          <div class="stat-item"><span>Files:</span><span class="stat-value">${metrics.totalFiles}</span></div>
          <div class="stat-item"><span>Connections:</span><span class="stat-value">${metrics.totalEdges}</span></div>
          <div class="stat-item"><span>Lines:</span><span class="stat-value">${metrics.totalLines.toLocaleString()}</span></div>
        </div>
        ${saveButton}
        <div class="view-toggle">
          <button class="view-btn${currentView === 'graph' ? ' active' : ''}" data-view="graph">Graph</button>
          <button class="view-btn${currentView === 'tree' ? ' active' : ''}" data-view="tree">Tree</button>
          <button class="view-btn${currentView === 'metrics' ? ' active' : ''}" data-view="metrics">Metrics</button>
        </div>
      </header>

      <div class="viz-body">
        <aside class="viz-sidebar">
          <div class="sidebar-section">
            <h3>Filter by Type</h3>
            <div class="filter-chips" id="type-filters">${typeChips}</div>
          </div>
          <div class="sidebar-section">
            <h3>Filter by Directory</h3>
            <div class="filter-chips" id="dir-filters">${dirChips}</div>
          </div>
          <div class="sidebar-section">
            <h3>Legend</h3>
            <div class="legend-list">${legendItems}</div>
          </div>
          <div class="sidebar-section">
            <h3>Quick Stats</h3>
            <div class="quick-stats">${quickStats}</div>
          </div>
          ${timelapseSection}
        </aside>

        <main class="viz-main">
          <div class="view-container graph-view${currentView === 'graph' ? ' active' : ''}" id="graph-view">
            <div class="graph-controls">
              <button class="graph-btn" id="zoom-in" title="Zoom In">&#43;</button>
              <button class="graph-btn" id="zoom-out" title="Zoom Out">&#8722;</button>
              <button class="graph-btn" id="reset-view" title="Fit to View">&#8634;</button>
              <button class="graph-btn" id="toggle-labels" title="Show Labels">Aa</button>
              <button class="graph-btn" id="toggle-size" title="Size by Lines">&#9679;</button>
              <button class="graph-btn" id="toggle-orphans" title="Hide Orphans">&#128279;</button>
              <button class="graph-btn" id="toggle-cluster" title="Group by Type (click to cycle: Type → Folder → None)">&#127912;</button>
              <button class="graph-btn" id="unpin-all" title="Unpin All Nodes (drag to pin, double-click to unpin)">&#128204;</button>
              <span class="controls-divider"></span>
              <button class="graph-btn" id="export-svg" title="Export as SVG">SVG</button>
              <button class="graph-btn" id="export-png" title="Export as PNG">PNG</button>
            </div>
            <div class="graph-container" id="graph-container"></div>
          </div>
          <div class="view-container tree-view${currentView === 'tree' ? ' active' : ''}" id="tree-view"></div>
          <div class="view-container metrics-view${currentView === 'metrics' ? ' active' : ''}" id="metrics-view"></div>
        </main>

        <aside class="viz-details" id="details-panel">
          <div class="empty-state">
            <div class="empty-icon">&#128269;</div>
            <p>Select a file to view details</p>
          </div>
        </aside>
      </div>
    </div>
    <div class="tooltip" id="tooltip"></div>
  `);

  addStyles();
  addVisualizationStyles();

  // Setup event handlers
  setupVisualizationEvents();

  // Initialize views
  initGraphView();
  initTreeView();
  initMetricsView();
}

/**
 * Render shared visualization (public view)
 */
function renderSharedVisualization() {
  if (!state.graph || !state.sharedProjectInfo) {
    state.view = 'landing';
    state.isViewingShared = false;
    window.history.replaceState({}, '', '/');
    render();
    return;
  }

  const { metrics, circularDeps, externalPackages, clusters } = state.graph;
  const { fullName, ownerUsername, ownerAvatar } = state.sharedProjectInfo;

  // Build type filter chips
  const typeChips = Object.entries(metrics.filesByGroup)
    .sort(([,a], [,b]) => (b as number) - (a as number))
    .map(([type, count]) => {
      const color = groupColors[type] || groupColors.other;
      return `<div class="filter-chip" data-type="${escapeHTML(type)}">
        <span class="chip-color" style="background:${color}"></span>
        ${escapeHTML(type)}
        <span class="chip-count">(${count})</span>
      </div>`;
    }).join('');

  // Build legend
  const usedGroups = new Set(state.graph.nodes.map(n => n.group));
  const legendItems = Array.from(usedGroups).sort().map(group => {
    const color = groupColors[group] || groupColors.other;
    return `<div class="legend-item">
      <div class="legend-color" style="background:${color}"></div>
      <span>${escapeHTML(group)}</span>
    </div>`;
  }).join('');

  // Quick stats
  const quickStats = `
    <div class="stat-row"><span>Avg imports/file:</span><span>${metrics.avgImportsPerFile}</span></div>
    <div class="stat-row"><span>Avg lines/file:</span><span>${metrics.avgLinesPerFile}</span></div>
    <div class="stat-row"><span>External packages:</span><span>${externalPackages.length}</span></div>
    <div class="stat-row"><span>Circular deps:</span><span class="${circularDeps.length > 0 ? 'warning' : 'success'}">${circularDeps.length}</span></div>
  `;

  setHTML(app, `
    <div class="visualization full">
      <header class="viz-header shared-header">
        <a href="/" class="btn btn-text">&larr; Home</a>
        <div class="shared-info">
          <img src="${escapeHTML(ownerAvatar)}" alt="" class="shared-avatar">
          <div class="shared-meta">
            <h1>${escapeHTML(fullName)}</h1>
            <span class="shared-by">Shared by @${escapeHTML(ownerUsername)}</span>
          </div>
        </div>
        <div class="header-stats">
          <div class="stat-item"><span>Files:</span><span class="stat-value">${metrics.totalFiles}</span></div>
          <div class="stat-item"><span>Connections:</span><span class="stat-value">${metrics.totalEdges}</span></div>
          <div class="stat-item"><span>Lines:</span><span class="stat-value">${metrics.totalLines.toLocaleString()}</span></div>
        </div>
        <div class="view-toggle">
          <button class="view-btn${currentView === 'graph' ? ' active' : ''}" data-view="graph">Graph</button>
          <button class="view-btn${currentView === 'tree' ? ' active' : ''}" data-view="tree">Tree</button>
          <button class="view-btn${currentView === 'metrics' ? ' active' : ''}" data-view="metrics">Metrics</button>
        </div>
      </header>

      <div class="viz-body">
        <aside class="viz-sidebar">
          <div class="sidebar-section shared-cta">
            <h3>Create Your Own</h3>
            <p>Visualize your codebase with webwebweb.dev</p>
            <a href="/" class="btn btn-primary btn-small">Try it Free</a>
          </div>
          <div class="sidebar-section">
            <h3>Filter by Type</h3>
            <div class="filter-chips" id="type-filters">${typeChips}</div>
          </div>
          <div class="sidebar-section">
            <h3>Legend</h3>
            <div class="legend-list">${legendItems}</div>
          </div>
          <div class="sidebar-section">
            <h3>Quick Stats</h3>
            <div class="quick-stats">${quickStats}</div>
          </div>
        </aside>

        <main class="viz-main">
          <div class="view-container graph-view${currentView === 'graph' ? ' active' : ''}" id="graph-view">
            <div class="graph-controls">
              <button class="graph-btn" id="zoom-in" title="Zoom In">&#43;</button>
              <button class="graph-btn" id="zoom-out" title="Zoom Out">&#8722;</button>
              <button class="graph-btn" id="reset-view" title="Fit to View">&#8634;</button>
              <button class="graph-btn" id="toggle-labels" title="Show Labels">Aa</button>
              <button class="graph-btn" id="toggle-size" title="Size by Lines">&#9679;</button>
              <button class="graph-btn" id="toggle-orphans" title="Hide Orphans">&#128279;</button>
              <button class="graph-btn" id="toggle-cluster" title="Group by Type">&#127912;</button>
            </div>
            <div class="graph-container" id="graph-container"></div>
          </div>
          <div class="view-container tree-view${currentView === 'tree' ? ' active' : ''}" id="tree-view"></div>
          <div class="view-container metrics-view${currentView === 'metrics' ? ' active' : ''}" id="metrics-view"></div>
        </main>

        <aside class="viz-details" id="details-panel">
          <div class="empty-state">
            <div class="empty-icon">&#128269;</div>
            <p>Select a file to view details</p>
          </div>
        </aside>
      </div>
    </div>
    <div class="tooltip" id="tooltip"></div>
  `);

  addStyles();
  addVisualizationStyles();
  addSharedStyles();

  // Setup event handlers (simplified for shared view)
  setupSharedVisualizationEvents();

  // Initialize views
  initGraphView();
  initTreeView();
  initMetricsView();
}

/**
 * Add shared view styles
 */
function addSharedStyles() {
  if (document.getElementById('shared-styles')) return;

  const style = document.createElement('style');
  style.id = 'shared-styles';
  style.textContent = `
    .shared-header { justify-content: flex-start !important; gap: 20px; }
    .shared-info { display: flex; align-items: center; gap: 12px; flex: 1; }
    .shared-avatar { width: 32px; height: 32px; border-radius: 50%; }
    .shared-meta { display: flex; flex-direction: column; }
    .shared-meta h1 { font-size: 16px; margin: 0; }
    .shared-by { font-size: 12px; color: var(--text-secondary); }
    .shared-cta { background: linear-gradient(135deg, var(--accent-blue) 0%, var(--accent-purple) 100%); border-radius: 8px; padding: 16px !important; text-align: center; }
    .shared-cta h3 { color: white !important; margin-bottom: 8px; }
    .shared-cta p { color: rgba(255,255,255,0.8); font-size: 12px; margin-bottom: 12px; }
    .shared-cta .btn { background: white; color: var(--accent-blue); }
    .shared-cta .btn:hover { background: rgba(255,255,255,0.9); }
  `;

  document.head.appendChild(style);
}

/**
 * Setup shared visualization event handlers (simplified)
 */
function setupSharedVisualizationEvents() {
  // View toggle
  document.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const view = btn.getAttribute('data-view') as 'graph' | 'tree' | 'metrics';
      currentView = view;
      document.querySelectorAll('.view-container').forEach(v => v.classList.remove('active'));
      document.getElementById(`${view}-view`)?.classList.add('active');
    });
  });

  // Type filter chips (read-only visual feedback)
  document.querySelectorAll('#type-filters .filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const type = chip.getAttribute('data-type')!;
      if (activeFilters.types.has(type)) {
        activeFilters.types.delete(type);
        chip.classList.remove('active');
      } else {
        activeFilters.types.add(type);
        chip.classList.add('active');
      }
      applyFilters();
    });
  });
}

/**
 * Setup visualization event handlers
 */
function setupVisualizationEvents() {
  // Back button
  document.getElementById('viz-back')!.onclick = () => {
    destroyGraph();
    // Go back to projects if viewing a saved project, otherwise to landing
    if (state.currentProjectId) {
      state.view = 'projects';
      state.currentProjectId = null;
      loadProjects();
    } else {
      state.view = 'landing';
    }
    state.graph = null;
    state.nodeAnnotations = {};  // Clear annotations when leaving visualization
    currentView = 'graph';
    activeFilters = { types: new Set(), dirs: new Set() };
    render();
  };

  // Save project button (pro users only)
  const saveBtn = document.getElementById('save-project-btn');
  if (saveBtn) {
    saveBtn.onclick = saveProject;
  }

  // View toggle
  document.querySelectorAll('.view-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const view = btn.getAttribute('data-view') as 'graph' | 'tree' | 'metrics';
      currentView = view;
      document.querySelectorAll('.view-container').forEach(v => v.classList.remove('active'));
      document.getElementById(`${view}-view`)?.classList.add('active');
    });
  });

  // Search
  const searchInput = document.getElementById('search-input') as HTMLInputElement;
  if (searchInput) {
    searchInput.oninput = () => {
      searchNodes(searchInput.value);
    };
  }

  // Type filter chips
  document.querySelectorAll('#type-filters .filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const type = chip.getAttribute('data-type')!;
      if (activeFilters.types.has(type)) {
        activeFilters.types.delete(type);
        chip.classList.remove('active');
      } else {
        activeFilters.types.add(type);
        chip.classList.add('active');
      }
      applyFilters();
    });
  });

  // Directory filter chips
  document.querySelectorAll('#dir-filters .filter-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const dir = chip.getAttribute('data-dir')!;
      if (activeFilters.dirs.has(dir)) {
        activeFilters.dirs.delete(dir);
        chip.classList.remove('active');
      } else {
        activeFilters.dirs.add(dir);
        chip.classList.add('active');
      }
      applyFilters();
    });
  });

  // Time-lapse controls
  const playBtn = document.getElementById('play-btn');
  const resetBtn = document.getElementById('reset-timelapse');
  const slider = document.getElementById('commit-slider') as HTMLInputElement;

  if (playBtn && slider) {
    playBtn.onclick = () => {
      if (!state.timelapse.isReady) {
        console.warn('[www] Timelapse not ready');
        return;
      }

      state.isPlaying = !state.isPlaying;
      playBtn.textContent = state.isPlaying ? '⏸ Pause' : '▶ Play';

      if (state.isPlaying) {
        // If at end, restart from beginning
        if (state.currentCommitIndex >= state.timelapse.sortedCommits.length - 1) {
          state.currentCommitIndex = 0;
          slider.value = '0';
          console.log('[www] Starting timelapse from beginning');
        }
        updateTimelapseView(state.currentCommitIndex);
        playTimelapse(slider);
      } else if (state.timelapse.playbackTimer) {
        clearTimeout(state.timelapse.playbackTimer);
        state.timelapse.playbackTimer = null;
      }
    };

    slider.oninput = () => {
      // Stop playback when manually scrubbing
      if (state.isPlaying) {
        state.isPlaying = false;
        playBtn.textContent = '▶ Play';
        if (state.timelapse.playbackTimer) {
          clearTimeout(state.timelapse.playbackTimer);
          state.timelapse.playbackTimer = null;
        }
      }
      state.currentCommitIndex = parseInt(slider.value);
      updateTimelapseView(state.currentCommitIndex);
    };
  }

  if (resetBtn && slider) {
    resetBtn.onclick = () => {
      // Stop playback
      if (state.isPlaying) {
        state.isPlaying = false;
        if (playBtn) playBtn.textContent = '▶ Play';
        if (state.timelapse.playbackTimer) {
          clearTimeout(state.timelapse.playbackTimer);
          state.timelapse.playbackTimer = null;
        }
      }
      // Reset to latest commit (show all files)
      state.currentCommitIndex = state.timelapse.sortedCommits.length - 1;
      slider.value = String(state.currentCommitIndex);
      updateTimelapseView(state.currentCommitIndex);
      console.log('[www] Reset to latest commit');
    };
  }

  // Keyboard shortcuts
  const handleKeydown = (e: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in input fields
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

    switch (e.key.toLowerCase()) {
      case 'f': // Fit to view
        zoomToFit();
        break;
      case 'l': // Toggle labels
        document.getElementById('toggle-labels')?.click();
        break;
      case 'o': // Toggle orphans
        document.getElementById('toggle-orphans')?.click();
        break;
      case 'g': // Cycle cluster/group mode
        document.getElementById('toggle-cluster')?.click();
        break;
      case 's': // Toggle size mode
        document.getElementById('toggle-size')?.click();
        break;
      case 'u': // Unpin all
        document.getElementById('unpin-all')?.click();
        break;
      case '/': // Focus search
        e.preventDefault();
        searchInput?.focus();
        break;
      case 'escape': // Back / clear search
        if (document.activeElement === searchInput) {
          searchInput.value = '';
          searchInput.blur();
          searchNodes('');
        }
        break;
      case ' ': // Play/pause timelapse
        if (state.timelapse.isReady) {
          e.preventDefault();
          playBtn?.click();
        }
        break;
    }
  };

  document.addEventListener('keydown', handleKeydown);

  // Clean up on navigation away (store reference for removal)
  (window as any).__vizKeydownHandler = handleKeydown;
}

/**
 * Apply type/directory filters to the graph
 */
function applyFilters() {
  if (!state.graph) return;

  const visibleNodes = new Set<string>();
  state.graph.nodes.forEach(node => {
    let visible = true;
    if (activeFilters.types.size > 0 && !activeFilters.types.has(node.group)) visible = false;
    if (activeFilters.dirs.size > 0 && !activeFilters.dirs.has(node.directory)) visible = false;
    if (visible) visibleNodes.add(node.id);
  });

  // Update graph visibility through D3
  const d3 = (window as any).d3;
  if (d3) {
    d3.selectAll('.node').style('display', (d: any) => visibleNodes.has(d.id) ? 'block' : 'none');
    d3.selectAll('.link').style('display', (d: any) => {
      const srcId = typeof d.source === 'string' ? d.source : d.source.id;
      const tgtId = typeof d.target === 'string' ? d.target : d.target.id;
      return visibleNodes.has(srcId) && visibleNodes.has(tgtId) ? 'block' : 'none';
    });
  }
}

/**
 * Initialize the graph view
 */
async function initGraphView() {
  const graphContainer = document.getElementById('graph-container')!;
  await initGraph(graphContainer, state.graph!, state.fileHistories, handleNodeSelect);

  // Apply saved node positions if loading a saved project
  if (state.savedNodePositions) {
    // Small delay to let simulation stabilize
    setTimeout(() => {
      applyNodePositions(state.savedNodePositions!);
      state.savedNodePositions = null;
    }, 100);
  }

  // Graph control state - use saved settings if available
  const settings = state.currentProjectSettings;
  let labelsOn = settings?.showLabels || false;
  let orphansHidden = settings?.hideOrphans || false;
  let clusterMode: 'none' | 'type' | 'folder' = settings?.defaultClustering || 'none';
  let sizeMode: 'lines' | 'connections' = settings?.sizeMode || 'lines';

  const d3 = (window as any).d3;
  const svg = d3?.select('#graph-container svg');

  // Get the zoom behavior from the SVG (set up in initGraph)
  const zoomIn = document.getElementById('zoom-in');
  const zoomOut = document.getElementById('zoom-out');
  const resetView = document.getElementById('reset-view');
  const toggleLabelsBtn = document.getElementById('toggle-labels');
  const toggleSizeBtn = document.getElementById('toggle-size');
  const toggleOrphansBtn = document.getElementById('toggle-orphans');
  const toggleClusterBtn = document.getElementById('toggle-cluster');

  // Apply saved project settings after graph initialization
  if (settings) {
    setTimeout(() => {
      // Apply labels setting
      if (labelsOn) {
        toggleLabels(true);
        if (toggleLabelsBtn) {
          toggleLabelsBtn.classList.add('active');
          toggleLabelsBtn.title = 'Hide Labels';
        }
      }

      // Apply orphans setting
      if (orphansHidden) {
        toggleOrphans(true);
        if (toggleOrphansBtn) {
          toggleOrphansBtn.classList.add('active');
          toggleOrphansBtn.title = 'Show Orphans';
        }
      }

      // Apply size mode setting
      if (sizeMode === 'connections') {
        setSizeMode('connections');
        if (toggleSizeBtn) {
          toggleSizeBtn.innerHTML = '&#8596;';
          toggleSizeBtn.title = 'Size by Connections';
        }
      }

      // Apply clustering setting
      if (clusterMode !== 'none') {
        setClusterMode(clusterMode);
        if (toggleClusterBtn) {
          toggleClusterBtn.classList.add('active');
          if (clusterMode === 'type') {
            toggleClusterBtn.innerHTML = '&#127912;'; // palette
            toggleClusterBtn.title = 'Grouped by Type (click for Folder grouping)';
          } else {
            toggleClusterBtn.innerHTML = '&#128193;'; // folder
            toggleClusterBtn.title = 'Grouped by Folder (click to disable grouping)';
          }
        }
      }

      // Clear the settings after applying
      state.currentProjectSettings = null;
    }, 150);
  }

  if (zoomIn) {
    zoomIn.onclick = () => {
      if (d3 && svg) {
        const currentTransform = d3.zoomTransform(svg.node());
        svg.transition().duration(300).call(
          d3.zoom().transform,
          d3.zoomIdentity.translate(currentTransform.x, currentTransform.y).scale(currentTransform.k * 1.3)
        );
      }
    };
  }

  if (zoomOut) {
    zoomOut.onclick = () => {
      if (d3 && svg) {
        const currentTransform = d3.zoomTransform(svg.node());
        svg.transition().duration(300).call(
          d3.zoom().transform,
          d3.zoomIdentity.translate(currentTransform.x, currentTransform.y).scale(currentTransform.k * 0.7)
        );
      }
    };
  }

  if (resetView) {
    resetView.onclick = () => {
      zoomToFit();
    };
  }

  if (toggleLabelsBtn) {
    toggleLabelsBtn.onclick = () => {
      labelsOn = !labelsOn;
      toggleLabels(labelsOn);
      toggleLabelsBtn.classList.toggle('active', labelsOn);
      toggleLabelsBtn.title = labelsOn ? 'Hide Labels' : 'Show Labels';
    };
  }

  if (toggleSizeBtn) {
    toggleSizeBtn.onclick = () => {
      sizeMode = sizeMode === 'lines' ? 'connections' : 'lines';
      setSizeMode(sizeMode);
      toggleSizeBtn.innerHTML = sizeMode === 'lines' ? '&#9679;' : '&#8596;';
      toggleSizeBtn.title = sizeMode === 'lines' ? 'Size by Lines' : 'Size by Connections';
    };
  }

  if (toggleOrphansBtn) {
    toggleOrphansBtn.onclick = () => {
      orphansHidden = !orphansHidden;
      toggleOrphans(orphansHidden);
      toggleOrphansBtn.classList.toggle('active', orphansHidden);
      toggleOrphansBtn.title = orphansHidden ? 'Show Orphans' : 'Hide Orphans';
    };
  }

  if (toggleClusterBtn) {
    toggleClusterBtn.onclick = () => {
      // Cycle through: none → type → folder → none
      if (clusterMode === 'none') {
        clusterMode = 'type';
        toggleClusterBtn.innerHTML = '&#127912;'; // palette
        toggleClusterBtn.title = 'Grouped by Type (click for Folder grouping)';
        toggleClusterBtn.classList.add('active');
      } else if (clusterMode === 'type') {
        clusterMode = 'folder';
        toggleClusterBtn.innerHTML = '&#128193;'; // folder
        toggleClusterBtn.title = 'Grouped by Folder (click to disable grouping)';
      } else {
        clusterMode = 'none';
        toggleClusterBtn.innerHTML = '&#127912;'; // palette
        toggleClusterBtn.title = 'Group by Type (click to cycle: Type → Folder → None)';
        toggleClusterBtn.classList.remove('active');
      }
      setClusterMode(clusterMode);
    };
  }

  const unpinAllBtn = document.getElementById('unpin-all');
  if (unpinAllBtn) {
    unpinAllBtn.onclick = () => {
      unpinAll();
    };
  }

  // Export buttons
  const exportSvgBtn = document.getElementById('export-svg');
  if (exportSvgBtn) {
    exportSvgBtn.onclick = () => {
      const svgString = exportToSVG();
      if (svgString) {
        const blob = new Blob([svgString], { type: 'image/svg+xml' });
        downloadBlob(blob, `${state.selectedRepo?.name || 'graph'}.svg`);
      }
    };
  }

  const exportPngBtn = document.getElementById('export-png');
  if (exportPngBtn) {
    exportPngBtn.onclick = async () => {
      exportPngBtn.textContent = '...';
      const blob = await exportToPNG(2);
      exportPngBtn.textContent = 'PNG';
      if (blob) {
        downloadBlob(blob, `${state.selectedRepo?.name || 'graph'}.png`);
      }
    };
  }
}

/**
 * Helper to download a blob as a file
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Initialize the tree view
 */
function initTreeView() {
  if (!state.graph) return;

  const treeContainer = document.getElementById('tree-view')!;

  // Build tree structure
  const tree: Record<string, any> = {};
  state.graph.nodes.forEach(node => {
    const parts = node.id.split('/');
    let current = tree;
    parts.forEach((part, i) => {
      if (!current[part]) {
        current[part] = i === parts.length - 1 ? { __node: node } : {};
      }
      current = current[part];
    });
  });

  // Render tree
  function renderTree(obj: Record<string, any>, depth: number): string {
    let html = '';
    Object.keys(obj).sort().forEach(key => {
      if (key === '__node') return;
      const isFile = !!obj[key].__node;
      const nodeData = obj[key].__node;
      const color = isFile ? (groupColors[nodeData.group] || groupColors.other) : '';

      html += `<div class="tree-node" style="padding-left:${depth * 16}px">
        <div class="tree-item${isFile ? '' : ' folder'}" data-id="${isFile ? escapeHTML(nodeData.id) : ''}">
          <span class="tree-toggle">${isFile ? '' : '&#9654;'}</span>
          <span class="tree-name">${escapeHTML(key)}</span>
          ${isFile ? `<span class="tree-type" style="color:${color}">${escapeHTML(nodeData.group)}</span>` : ''}
        </div>
        ${!isFile ? `<div class="tree-children" style="display:none">${renderTree(obj[key], depth + 1)}</div>` : ''}
      </div>`;
    });
    return html;
  }

  setHTML(treeContainer, renderTree(tree, 0));

  // Add click handlers
  treeContainer.querySelectorAll('.tree-item').forEach(item => {
    const id = item.getAttribute('data-id');
    if (id) {
      // File click
      item.addEventListener('click', () => {
        const node = state.graph?.nodes.find(n => n.id === id);
        if (node) {
          handleNodeSelect(node);
          treeContainer.querySelectorAll('.tree-item').forEach(el => el.classList.remove('selected'));
          item.classList.add('selected');
        }
      });
    } else {
      // Folder toggle
      const toggle = item.querySelector('.tree-toggle');
      if (toggle) {
        toggle.addEventListener('click', (e) => {
          e.stopPropagation();
          const children = item.parentElement?.querySelector('.tree-children') as HTMLElement;
          if (children) {
            const isOpen = children.style.display !== 'none';
            children.style.display = isOpen ? 'none' : 'block';
            toggle.textContent = isOpen ? '\u25B6' : '\u25BC';
          }
        });
      }
    }
  });
}

/**
 * Initialize the metrics view
 */
function initMetricsView() {
  if (!state.graph) return;

  const metricsContainer = document.getElementById('metrics-view')!;
  const { metrics, circularDeps, externalPackages } = state.graph;

  let html = `
    <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-label">Total Files</div>
        <div class="metric-value">${metrics.totalFiles.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Total Lines</div>
        <div class="metric-value">${metrics.totalLines.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">Connections</div>
        <div class="metric-value">${metrics.totalEdges.toLocaleString()}</div>
      </div>
      <div class="metric-card">
        <div class="metric-label">External Packages</div>
        <div class="metric-value">${externalPackages.length}</div>
      </div>
    </div>
  `;

  // Circular dependencies warning
  if (circularDeps.length > 0) {
    html += `
      <div class="circular-warning">
        <div class="warning-title">&#9888; Circular Dependencies (${circularDeps.length})</div>
        ${circularDeps.slice(0, 5).map(cycle =>
          `<div class="circular-path">${cycle.map(escapeHTML).join(' &rarr; ')}</div>`
        ).join('')}
        ${circularDeps.length > 5 ? `<div class="more-text">... and ${circularDeps.length - 5} more</div>` : ''}
      </div>
    `;
  }

  // Most imported files
  html += `
    <div class="metric-list">
      <div class="metric-list-title">Most Imported Files (Hubs)</div>
      ${metrics.mostImported.slice(0, 10).map(f =>
        `<div class="metric-list-item">
          <span class="metric-name" data-id="${escapeHTML(f.id)}">${escapeHTML(f.name)}</span>
          <span>${f.imports} imports</span>
        </div>`
      ).join('')}
    </div>
  `;

  // Most dependencies
  html += `
    <div class="metric-list">
      <div class="metric-list-title">Most Dependencies</div>
      ${metrics.mostDependencies.slice(0, 10).map(f =>
        `<div class="metric-list-item">
          <span class="metric-name" data-id="${escapeHTML(f.id)}">${escapeHTML(f.name)}</span>
          <span>${f.dependencies} deps</span>
        </div>`
      ).join('')}
    </div>
  `;

  // Largest files
  html += `
    <div class="metric-list">
      <div class="metric-list-title">Largest Files</div>
      ${metrics.largestFiles.slice(0, 10).map(f =>
        `<div class="metric-list-item">
          <span class="metric-name" data-id="${escapeHTML(f.id)}">${escapeHTML(f.name)}</span>
          <span>${f.lines.toLocaleString()} lines</span>
        </div>`
      ).join('')}
    </div>
  `;

  // Top external packages
  html += `
    <div class="metric-list">
      <div class="metric-list-title">Top External Packages</div>
      ${metrics.topExternalPackages.slice(0, 10).map(p =>
        `<div class="metric-list-item">
          <span>${escapeHTML(p.name)}</span>
          <span>${p.count} uses</span>
        </div>`
      ).join('')}
    </div>
  `;

  // Orphan files
  if (metrics.orphans.length > 0) {
    html += `
      <div class="metric-list orphans">
        <div class="metric-list-title">Orphan Files (No Connections)</div>
        ${metrics.orphans.slice(0, 10).map(f =>
          `<div class="metric-list-item">
            <span class="metric-name" data-id="${escapeHTML(f)}">${escapeHTML(f.split('/').pop() || f)}</span>
          </div>`
        ).join('')}
        ${metrics.orphans.length > 10 ? `<div class="more-text">... and ${metrics.orphans.length - 10} more</div>` : ''}
      </div>
    `;
  }

  setHTML(metricsContainer, html);

  // Add click handlers for file names
  metricsContainer.querySelectorAll('.metric-name').forEach(el => {
    el.addEventListener('click', () => {
      const id = el.getAttribute('data-id');
      if (id) {
        const node = state.graph?.nodes.find(n => n.id === id);
        if (node) {
          currentView = 'graph';
          document.querySelectorAll('.view-btn').forEach(b => b.classList.remove('active'));
          document.querySelector('.view-btn[data-view="graph"]')?.classList.add('active');
          document.querySelectorAll('.view-container').forEach(v => v.classList.remove('active'));
          document.getElementById('graph-view')?.classList.add('active');
          handleNodeSelect(node);
        }
      }
    });
  });
}

/**
 * Handle node selection - show details panel
 */
function handleNodeSelect(node: GraphNode | null) {
  const panel = document.getElementById('details-panel')!;

  if (!node) {
    setHTML(panel, `
      <div class="empty-state">
        <div class="empty-icon">&#128269;</div>
        <p>Select a file to view details</p>
      </div>
    `);
    return;
  }

  // Get incoming and outgoing edges
  const incoming = state.graph?.edges.filter(e => {
    const targetId = typeof e.target === 'string' ? e.target : (e.target as any).id;
    return targetId === node.id;
  }) || [];

  const outgoing = state.graph?.edges.filter(e => {
    const sourceId = typeof e.source === 'string' ? e.source : (e.source as any).id;
    return sourceId === node.id;
  }) || [];

  const fileHistory = state.fileHistories.get(node.id);
  const historyInfo = fileHistory ? `
    <div class="detail-section">
      <h4>History</h4>
      <div class="detail-row">
        <span>Created</span>
        <span>${new Date(fileHistory.firstCommit.author.timestamp * 1000).toLocaleDateString()}</span>
      </div>
      <div class="detail-row">
        <span>Last Modified</span>
        <span>${new Date(fileHistory.lastCommit.author.timestamp * 1000).toLocaleDateString()}</span>
      </div>
      <div class="detail-row">
        <span>Commits</span>
        <span>${fileHistory.commitCount}</span>
      </div>
    </div>
  ` : '';

  // Exports with type tags
  const exportsHtml = node.exports.length > 0 ? `
    <div class="detail-section">
      <h4>Exports (${node.exports.length})</h4>
      <div class="export-list">
        ${node.exports.slice(0, 15).map(e =>
          `<span class="export-tag ${e.type}">${escapeHTML(e.name)}</span>`
        ).join('')}
        ${node.exports.length > 15 ? `<span class="more-text">+${node.exports.length - 15} more</span>` : ''}
      </div>
    </div>
  ` : '';

  // Imports (outgoing connections)
  const importsHtml = outgoing.length > 0 ? `
    <div class="detail-section">
      <h4>Imports (${outgoing.length})</h4>
      <div class="connection-list">
        ${outgoing.slice(0, 10).map(e => {
          const targetId = typeof e.target === 'string' ? e.target : (e.target as any).id;
          const name = targetId.split('/').pop() || targetId;
          return `<div class="connection-item" data-id="${escapeHTML(targetId)}">
            <span class="arrow out">&rarr;</span>
            <span>${escapeHTML(name)}</span>
          </div>`;
        }).join('')}
        ${outgoing.length > 10 ? `<div class="more-text">+${outgoing.length - 10} more</div>` : ''}
      </div>
    </div>
  ` : '';

  // Imported by (incoming connections)
  const importedByHtml = incoming.length > 0 ? `
    <div class="detail-section">
      <h4>Imported By (${incoming.length})</h4>
      <div class="connection-list">
        ${incoming.slice(0, 10).map(e => {
          const sourceId = typeof e.source === 'string' ? e.source : (e.source as any).id;
          const name = sourceId.split('/').pop() || sourceId;
          return `<div class="connection-item" data-id="${escapeHTML(sourceId)}">
            <span class="arrow in">&larr;</span>
            <span>${escapeHTML(name)}</span>
          </div>`;
        }).join('')}
        ${incoming.length > 10 ? `<div class="more-text">+${incoming.length - 10} more</div>` : ''}
      </div>
    </div>
  ` : '';

  const nodeColor = groupColors[node.group] || groupColors.other;
  const orphanBadge = (node.inDegree === 0 && node.outDegree === 0)
    ? '<span class="orphan-badge">ORPHAN</span>' : '';

  // Get existing annotation for this node
  const existingNote = state.nodeAnnotations[node.id] || '';
  const isPro = state.session?.user?.isPro;

  // Notes section (Pro feature)
  const notesHtml = isPro ? `
    <div class="detail-section notes-section">
      <h4>Notes</h4>
      <textarea
        id="node-note-input"
        class="note-textarea"
        placeholder="Add notes about this file..."
        rows="3"
      >${escapeHTML(existingNote)}</textarea>
      <div class="note-actions">
        <button id="save-note-btn" class="btn btn-small btn-primary">Save Note</button>
        ${existingNote ? '<button id="clear-note-btn" class="btn btn-small btn-text">Clear</button>' : ''}
      </div>
    </div>
  ` : '';

  setHTML(panel, `
    <div class="node-details">
      <div class="details-header">
        <div class="details-title">
          <span class="node-color" style="background: ${nodeColor}"></span>
          ${escapeHTML(node.name)}
          ${orphanBadge}
        </div>
        <div class="details-path">${escapeHTML(node.id)}</div>
      </div>

      <div class="detail-section">
        <h4>Info</h4>
        <div class="details-grid">
          <div class="detail-item">
            <div class="detail-label">Lines</div>
            <div class="detail-value">${node.lineCount.toLocaleString()}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Type</div>
            <div class="detail-value" style="color:${nodeColor}">${escapeHTML(node.group)}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Imports</div>
            <div class="detail-value">${node.outDegree}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">Imported by</div>
            <div class="detail-value">${node.inDegree}</div>
          </div>
        </div>
      </div>

      ${notesHtml}
      ${historyInfo}
      ${exportsHtml}
      ${importsHtml}
      ${importedByHtml}
    </div>
  `);

  // Add click handlers for connection items
  panel.querySelectorAll('.connection-item').forEach(item => {
    item.addEventListener('click', () => {
      const id = item.getAttribute('data-id');
      if (id) {
        const targetNode = state.graph?.nodes.find(n => n.id === id);
        if (targetNode) {
          handleNodeSelect(targetNode);
        }
      }
    });
  });

  // Note handlers (Pro feature)
  const saveNoteBtn = document.getElementById('save-note-btn');
  const clearNoteBtn = document.getElementById('clear-note-btn');
  const noteInput = document.getElementById('node-note-input') as HTMLTextAreaElement;

  if (saveNoteBtn && noteInput) {
    saveNoteBtn.onclick = () => {
      const noteText = noteInput.value.trim();
      if (noteText) {
        state.nodeAnnotations[node.id] = noteText;
      } else {
        delete state.nodeAnnotations[node.id];
      }
      // Visual feedback
      saveNoteBtn.textContent = 'Saved!';
      setTimeout(() => {
        saveNoteBtn.textContent = 'Save Note';
      }, 1500);
    };
  }

  if (clearNoteBtn && noteInput) {
    clearNoteBtn.onclick = () => {
      delete state.nodeAnnotations[node.id];
      noteInput.value = '';
      clearNoteBtn.remove();
    };
  }
}

// Timelapse data is now built during commit analysis in analyzeGitHubRepo
// using buildTimelapseFromCommits from history.ts

/**
 * Update graph visibility based on commit index
 */
function updateTimelapseView(commitIndex: number) {
  const d3 = (window as any).d3;
  if (!d3 || !state.timelapse.sortedCommits.length) {
    console.warn('[www] Cannot update timelapse: d3 or commits not available');
    return;
  }

  const commit = state.timelapse.sortedCommits[commitIndex];
  if (!commit) {
    console.warn(`[www] No commit found at index ${commitIndex}`);
    return;
  }

  const visibleFiles = state.timelapse.filesByCommit.get(commit.oid);
  if (!visibleFiles) {
    console.warn(`[www] No visibility data for commit ${commit.oid.slice(0, 7)}`);
    return;
  }

  const changes = state.timelapse.changesByCommit.get(commit.oid) || { added: [], modified: [], deleted: [] };

  // Debug logging (only for significant changes)
  if (commitIndex % 10 === 0 || changes.added.length > 0 || changes.deleted.length > 0) {
    console.log(`[www] Commit ${commitIndex}: ${visibleFiles.size} visible, +${changes.added.length}/-${changes.deleted.length}`);
  }

  // Clear previous effects
  state.timelapse.recentlyAdded.clear();
  state.timelapse.recentlyModified.clear();
  state.timelapse.recentlyDeleted.clear();

  // Set new effects
  changes.added.forEach(f => state.timelapse.recentlyAdded.add(f));
  changes.modified.forEach(f => state.timelapse.recentlyModified.add(f));
  changes.deleted.forEach(f => state.timelapse.recentlyDeleted.add(f));

  // Update node visibility and effects
  let visibleCount = 0;
  d3.selectAll('.node').each(function(d: any) {
    const el = d3.select(this);
    const circle = el.select('circle');
    const isVisible = visibleFiles.has(d.id);
    const isAdded = state.timelapse.recentlyAdded.has(d.id);
    const isModified = state.timelapse.recentlyModified.has(d.id);

    if (isVisible) visibleCount++;

    // Remove all effect classes first
    el.classed('timelapse-added', false)
      .classed('timelapse-modified', false)
      .classed('timelapse-ghost', false);

    if (isAdded && isVisible) {
      // Newly added file - animate appearance
      el.classed('timelapse-added', true);
      el.style('opacity', 1)
        .style('pointer-events', 'auto');

      // Pulse animation using D3
      const originalR = circle.attr('r');
      circle
        .attr('r', 0)
        .transition()
        .duration(200)
        .attr('r', parseFloat(originalR) * 1.5)
        .transition()
        .duration(200)
        .attr('r', originalR);

    } else if (isModified && isVisible) {
      // Modified file - glow effect
      el.classed('timelapse-modified', true);
      el.style('opacity', 1)
        .style('pointer-events', 'auto');

    } else if (isVisible) {
      // Normal visible file
      el.style('opacity', 1)
        .style('pointer-events', 'auto');
    } else {
      // Hidden file
      el.style('opacity', 0)
        .style('pointer-events', 'none');
    }
  });

  // Show ghosts for recently deleted (briefly visible then fade)
  changes.deleted.forEach(filePath => {
    const node = d3.selectAll('.node').filter((d: any) => d.id === filePath);
    if (!node.empty()) {
      const circle = node.select('circle');
      node.classed('timelapse-ghost', true)
        .style('opacity', 0.7)
        .style('pointer-events', 'none');

      // Shrink and fade out
      const originalR = circle.attr('r');
      circle
        .transition()
        .duration(state.timelapse.playbackSpeed * 0.7)
        .attr('r', 0);

      node.transition()
        .duration(state.timelapse.playbackSpeed * 0.7)
        .style('opacity', 0)
        .on('end', function() {
          circle.attr('r', originalR);
        });
    }
  });

  // Update link visibility
  d3.selectAll('.link').each(function(d: any) {
    const el = d3.select(this);
    const srcId = typeof d.source === 'string' ? d.source : d.source.id;
    const tgtId = typeof d.target === 'string' ? d.target : d.target.id;
    const isVisible = visibleFiles.has(srcId) && visibleFiles.has(tgtId);
    el.style('opacity', isVisible ? 0.3 : 0);
  });

  // Update commit info display
  updateCommitInfo();
}

/**
 * Play time-lapse animation (oldest to newest)
 */
function playTimelapse(slider: HTMLInputElement) {
  if (!state.isPlaying) return;

  const next = state.currentCommitIndex + 1;
  if (next >= state.timelapse.sortedCommits.length) {
    // Reached the end
    state.isPlaying = false;
    const playBtn = document.getElementById('play-btn');
    if (playBtn) playBtn.textContent = '▶ Play';
    if (state.timelapse.playbackTimer) {
      clearTimeout(state.timelapse.playbackTimer);
      state.timelapse.playbackTimer = null;
    }
    console.log('[www] Timelapse playback complete');
    return;
  }

  slider.value = String(next);
  state.currentCommitIndex = next;
  updateTimelapseView(next);

  state.timelapse.playbackTimer = window.setTimeout(
    () => playTimelapse(slider),
    state.timelapse.playbackSpeed
  );
}

/**
 * Update commit info display
 */
function updateCommitInfo() {
  const info = document.getElementById('commit-info');
  const commit = state.timelapse.sortedCommits[state.currentCommitIndex];

  if (info && commit) {
    const date = new Date(commit.author.timestamp * 1000);
    const changes = state.timelapse.changesByCommit.get(commit.oid);
    const changeText = changes
      ? ` (+${changes.added.length} ~${changes.modified.length} -${changes.deleted.length})`
      : '';

    info.textContent = `${date.toLocaleDateString()} - ${commit.message.split('\n')[0].slice(0, 25)}${changeText}`;
  }
}

/**
 * Add visualization-specific styles
 */
function addVisualizationStyles() {
  if (document.getElementById('viz-styles')) return;

  const style = document.createElement('style');
  style.id = 'viz-styles';
  style.textContent = `
    .visualization.full { height: 100vh; }
    .viz-header { display: flex; align-items: center; gap: 16px; padding: 0 20px; height: 56px; background: var(--bg-secondary); border-bottom: 1px solid var(--border-color); }
    .viz-header h1 { font-size: 18px; font-weight: 600; }
    .search-container { flex: 1; max-width: 400px; }
    .search-input { width: 100%; padding: 8px 12px; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-primary); font-size: 14px; }
    .search-input:focus { outline: none; border-color: var(--accent-blue); }
    .header-stats { display: flex; gap: 16px; font-size: 13px; color: var(--text-secondary); }
    .stat-item { display: flex; align-items: center; gap: 6px; }
    .stat-value { color: var(--text-primary); font-weight: 500; }
    .view-toggle { display: flex; gap: 4px; background: var(--bg-tertiary); padding: 4px; border-radius: 6px; }
    .view-btn { padding: 6px 12px; border: none; background: transparent; color: var(--text-secondary); border-radius: 4px; cursor: pointer; font-size: 13px; }
    .view-btn.active { background: var(--accent-blue); color: white; }
    .view-btn:hover:not(.active) { background: var(--bg-secondary); color: var(--text-primary); }
    .viz-body { display: grid; grid-template-columns: 280px 1fr 320px; flex: 1; min-height: 0; }
    .viz-sidebar { background: var(--bg-secondary); border-right: 1px solid var(--border-color); padding: 16px; overflow-y: auto; }
    .sidebar-section { margin-bottom: 24px; }
    .sidebar-section h3 { font-size: 11px; font-weight: 600; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 12px; }
    .filter-chips { display: flex; flex-wrap: wrap; gap: 6px; }
    .filter-chip { padding: 4px 10px; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 12px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 4px; }
    .filter-chip:hover { border-color: var(--accent-blue); }
    .filter-chip.active { background: var(--accent-blue); border-color: var(--accent-blue); color: white; }
    .chip-color { width: 8px; height: 8px; border-radius: 2px; }
    .chip-count { font-size: 10px; opacity: 0.7; }
    .legend-list { display: flex; flex-direction: column; gap: 4px; }
    .legend-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 13px; }
    .legend-color { width: 12px; height: 12px; border-radius: 3px; }
    .quick-stats { display: flex; flex-direction: column; gap: 6px; }
    .stat-row { display: flex; justify-content: space-between; font-size: 13px; }
    .stat-row span:first-child { color: var(--text-secondary); }
    .stat-row .warning { color: var(--accent-red); }
    .stat-row .success { color: var(--accent-green); }
    .viz-main { background: var(--bg-primary); overflow: hidden; position: relative; }
    .view-container { display: none; height: 100%; width: 100%; }
    .view-container.active { display: block; }
    .graph-view { position: relative; }
    .graph-controls { position: absolute; bottom: 20px; left: 20px; display: flex; gap: 6px; z-index: 10; background: var(--bg-secondary); padding: 6px; border-radius: 8px; border: 1px solid var(--border-color); }
    .graph-btn { min-width: 36px; height: 36px; padding: 0 10px; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-primary); cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; transition: all 0.15s ease; }
    .graph-btn:hover { background: var(--bg-tertiary); border-color: var(--accent-blue); }
    .graph-btn.active { background: var(--accent-blue); border-color: var(--accent-blue); color: white; }
    .controls-divider { width: 1px; height: 24px; background: var(--border-color); margin: 0 4px; }
    .graph-container { width: 100%; height: 100%; }
    .tree-view { padding: 16px; overflow-y: auto; }
    .tree-node { }
    .tree-item { display: flex; align-items: center; gap: 6px; padding: 4px 8px; cursor: pointer; border-radius: 4px; font-size: 13px; }
    .tree-item:hover { background: var(--bg-tertiary); }
    .tree-item.selected { background: var(--accent-blue); color: white; }
    .tree-toggle { width: 16px; height: 16px; display: flex; align-items: center; justify-content: center; color: var(--text-secondary); }
    .tree-type { font-size: 10px; margin-left: auto; }
    .tree-children { }
    .metrics-view { padding: 20px; overflow-y: auto; }
    .metrics-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 16px; margin-bottom: 24px; }
    .metric-card { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; }
    .metric-label { font-size: 12px; color: var(--text-secondary); margin-bottom: 4px; }
    .metric-value { font-size: 24px; font-weight: 600; }
    .circular-warning { background: rgba(248, 81, 73, 0.1); border: 1px solid var(--accent-red); border-radius: 8px; padding: 16px; margin-bottom: 16px; }
    .warning-title { color: var(--accent-red); font-weight: 600; margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
    .circular-path { font-family: monospace; font-size: 12px; color: var(--text-secondary); padding: 8px; background: var(--bg-tertiary); border-radius: 4px; margin-top: 4px; overflow-x: auto; }
    .metric-list { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 8px; padding: 16px; margin-bottom: 16px; }
    .metric-list-title { font-size: 14px; font-weight: 600; margin-bottom: 12px; }
    .metric-list-item { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid var(--border-color); font-size: 13px; }
    .metric-list-item:last-child { border-bottom: none; }
    .metric-name { color: var(--text-secondary); cursor: pointer; }
    .metric-name:hover { color: var(--accent-blue); }
    .more-text { color: var(--text-secondary); font-size: 12px; margin-top: 4px; }
    .viz-details { background: var(--bg-secondary); border-left: 1px solid var(--border-color); padding: 16px; overflow-y: auto; }
    .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 200px; color: var(--text-secondary); text-align: center; }
    .empty-icon { font-size: 32px; margin-bottom: 12px; opacity: 0.5; }
    .details-header { margin-bottom: 16px; }
    .details-title { font-size: 16px; font-weight: 600; display: flex; align-items: center; gap: 8px; word-break: break-all; margin-bottom: 4px; }
    .node-color { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
    .orphan-badge { font-size: 10px; background: var(--accent-red); color: white; padding: 2px 6px; border-radius: 4px; }
    .details-path { font-size: 12px; color: var(--text-secondary); word-break: break-all; }
    .detail-section { margin-bottom: 20px; }
    .detail-section h4 { font-size: 12px; font-weight: 600; color: var(--text-secondary); margin-bottom: 8px; text-transform: uppercase; }
    .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .detail-item { background: var(--bg-tertiary); padding: 10px; border-radius: 6px; }
    .detail-label { font-size: 11px; color: var(--text-secondary); margin-bottom: 2px; }
    .detail-value { font-size: 14px; font-weight: 500; }
    .detail-row { display: flex; justify-content: space-between; font-size: 13px; padding: 4px 0; }
    .export-list { display: flex; flex-wrap: wrap; gap: 6px; }
    .export-tag { padding: 3px 8px; background: var(--bg-tertiary); border-radius: 4px; font-size: 11px; font-family: monospace; }
    .export-tag.default { border: 1px solid var(--accent-green); color: var(--accent-green); }
    .connection-list { max-height: 200px; overflow-y: auto; }
    .connection-item { padding: 8px 10px; background: var(--bg-tertiary); border-radius: 4px; margin-bottom: 4px; font-size: 12px; cursor: pointer; display: flex; align-items: center; gap: 8px; }
    .connection-item:hover { background: var(--bg-primary); border-left: 2px solid var(--accent-blue); }
    .arrow.out { color: var(--accent-green); }
    .arrow.in { color: var(--accent-orange); }
    .tooltip { position: absolute; background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 6px; padding: 10px 14px; font-size: 12px; pointer-events: none; z-index: 1000; max-width: 300px; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3); display: none; }
    .node circle { stroke: rgba(0,0,0,0.7); stroke-width: 2px; cursor: pointer; transition: all 0.2s ease; }
    .node:hover circle { stroke: #fff; stroke-width: 3px; filter: drop-shadow(0 0 10px currentColor); }
    .node.selected circle { stroke: #fff; stroke-width: 5px; filter: drop-shadow(0 0 15px #fff) drop-shadow(0 0 25px #00d4ff); }
    .node.connected-out circle { stroke: #30d158; stroke-width: 4px; filter: drop-shadow(0 0 12px #30d158); }
    .node.connected-in circle { stroke: #ff9f0a; stroke-width: 4px; filter: drop-shadow(0 0 12px #ff9f0a); }
    .node.orphan circle { opacity: 0.35; stroke: #ff453a; stroke-dasharray: 4,3; }
    .node.dimmed circle { opacity: 0.05; }
    .node.dimmed text { opacity: 0.05; }
    .node text { font-size: 10px; fill: var(--text-secondary); pointer-events: none; transition: opacity 0.2s; }
    .link { stroke: #555; stroke-opacity: 0.2; fill: none; transition: all 0.2s ease; }
    .link.highlighted-out { stroke: #30d158; stroke-opacity: 1; stroke-width: 3px; filter: drop-shadow(0 0 6px #30d158); }
    .link.highlighted-in { stroke: #ff9f0a; stroke-opacity: 1; stroke-width: 3px; filter: drop-shadow(0 0 6px #ff9f0a); }
    .link.dimmed { stroke-opacity: 0.02; }

    /* Timelapse styles */
    .timelapse-section { background: var(--bg-tertiary); border-radius: 8px; padding: 12px !important; }
    .timelapse-buttons { display: flex; gap: 8px; margin-bottom: 8px; }
    .timelapse-buttons .btn { flex: 1; }
    .timelapse-controls input[type="range"] { width: 100%; margin: 8px 0; }
    .commit-info { font-size: 11px; color: var(--text-secondary); text-align: center; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .timelapse-loading { display: flex; align-items: center; gap: 10px; padding: 8px 0; color: var(--text-secondary); font-size: 12px; }
    .timelapse-spinner { width: 16px; height: 16px; border: 2px solid var(--bg-secondary); border-top-color: var(--accent-blue); border-radius: 50%; animation: spin 1s linear infinite; }
    .timelapse-unavailable { padding: 8px 0; color: var(--text-secondary); font-size: 12px; opacity: 0.7; }

    .node.timelapse-added circle {
      animation: nodeAppear 0.5s ease-out;
      filter: drop-shadow(0 0 15px #30d158) drop-shadow(0 0 25px #30d158);
    }
    .node.timelapse-modified circle {
      animation: nodeGlow 0.5s ease-out;
      filter: drop-shadow(0 0 12px #ffd60a) drop-shadow(0 0 20px #ff9f0a);
    }
    .node.timelapse-ghost circle {
      stroke: #ff453a !important;
      stroke-dasharray: 4, 2;
      filter: drop-shadow(0 0 10px #ff453a);
      animation: nodeGhost 0.8s ease-out forwards;
    }

    @keyframes nodeAppear {
      0% { transform: scale(0); opacity: 0; }
      50% { transform: scale(1.3); }
      100% { transform: scale(1); opacity: 1; }
    }
    @keyframes nodeGlow {
      0% { filter: drop-shadow(0 0 0px #ffd60a); }
      50% { filter: drop-shadow(0 0 25px #ffd60a) drop-shadow(0 0 40px #ff9f0a); }
      100% { filter: drop-shadow(0 0 12px #ffd60a); }
    }
    @keyframes nodeGhost {
      0% { opacity: 0.6; transform: scale(1); }
      100% { opacity: 0; transform: scale(0.5); }
    }

    /* Notes section styles */
    .notes-section { background: var(--bg-tertiary); border-radius: 8px; padding: 12px !important; margin-bottom: 16px; }
    .note-textarea { width: 100%; padding: 10px; background: var(--bg-primary); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-primary); font-size: 13px; font-family: inherit; resize: vertical; min-height: 60px; }
    .note-textarea:focus { outline: none; border-color: var(--accent-blue); }
    .note-textarea::placeholder { color: var(--text-secondary); opacity: 0.6; }
    .note-actions { display: flex; gap: 8px; margin-top: 8px; justify-content: flex-end; }
    .btn-small { padding: 6px 12px; font-size: 12px; }
  `;

  document.head.appendChild(style);
}

/**
 * Add styles to the page
 */
function addStyles() {
  if (document.getElementById('www-styles')) return;

  const style = document.createElement('style');
  style.id = 'www-styles';
  style.textContent = `
    .landing { min-height: 100vh; display: flex; flex-direction: column; padding: 2rem; }
    .landing-header { display: flex; align-items: center; justify-content: center; margin-bottom: 1rem; position: relative; }
    .logo-container { display: flex; align-items: center; gap: 0.75rem; }
    .app-logo { display: flex; align-items: center; }
    .app-logo svg { width: 44px; height: 44px; }
    .header-user { display: flex; align-items: center; gap: 0.75rem; position: absolute; right: 0; top: 50%; transform: translateY(-50%); }
    .avatar-small { width: 28px; height: 28px; border-radius: 50%; }
    .logo { font-size: 2.5rem; font-weight: 700; font-family: 'JetBrains Mono', monospace; text-align: center; }
    .logo-tld { color: var(--accent-blue); }
    .hero { display: flex; flex-direction: column; align-items: center; gap: 1.5rem; margin: 2rem auto 2rem; max-width: 900px; width: 100%; }
    .hero-content { text-align: center; }
    .tagline { color: var(--text-primary); font-size: 2rem; font-weight: 600; margin-bottom: 0.5rem; }
    .hero-subtitle { color: var(--text-secondary); font-size: 1.1rem; }
    .hero-simulation { width: 280px; height: 280px; position: relative; border-radius: 16px; overflow: hidden; background: var(--bg-secondary); border: 1px solid var(--border-color); box-shadow: 0 4px 24px rgba(0,0,0,0.2); }
    .hero-simulation canvas { width: 100%; height: 100%; display: block; }
    .features { display: flex; gap: 2rem; justify-content: center; flex-wrap: wrap; margin: 0 auto 3rem; max-width: 900px; padding: 0 1rem; }
    .feature { text-align: center; flex: 1; min-width: 200px; max-width: 250px; }
    .feature-icon { font-size: 2rem; margin-bottom: 0.75rem; }
    .feature h3 { font-size: 1rem; font-weight: 600; margin-bottom: 0.5rem; color: var(--text-primary); }
    .feature p { font-size: 0.85rem; color: var(--text-secondary); line-height: 1.5; }
    .section-title { font-size: 1.25rem; font-weight: 600; color: var(--text-secondary); margin-bottom: 1.5rem; text-transform: uppercase; letter-spacing: 0.05em; }
    @media (max-width: 768px) {
      .hero-simulation { width: 200px; height: 200px; }
      .tagline { font-size: 1.25rem; }
      .hero-subtitle { font-size: 0.95rem; }
      .features { gap: 1rem; padding: 0 0.5rem; }
      .feature { min-width: 140px; max-width: 160px; }
      .feature-icon { font-size: 1.5rem; }
      .feature h3 { font-size: 0.9rem; }
      .feature p { font-size: 0.75rem; }
      .input-options { flex-direction: column; gap: 1rem; align-items: center; }
      .option-card { width: 100%; max-width: 320px; padding: 1.5rem; }
      .drop-zone { margin: 0 1rem; min-height: 120px; }
      .landing-header { padding: 0.75rem 1rem; }
      .logo-container { gap: 0.5rem; }
      .app-logo { width: 24px; height: 24px; }
      .logo { font-size: 1.1rem; }
      .header-user { gap: 0.5rem; font-size: 0.85rem; }
      .avatar-small { width: 24px; height: 24px; }
    }

    /* Visualization mobile styles */
    @media (max-width: 1024px) {
      .viz-header { flex-wrap: wrap; gap: 0.5rem; padding: 0.75rem 1rem; }
      .viz-header h1 { font-size: 1rem; flex: 1 1 auto; }
      .header-stats { display: none; }
      .search-container { order: 10; width: 100%; margin-top: 0.5rem; }
      .search-input { width: 100%; }
      .view-toggle { margin-left: 0; }
      .viz-body { flex-direction: column; }
      .viz-sidebar { width: 100%; max-height: 200px; overflow-y: auto; border-right: none; border-bottom: 1px solid var(--border-color); }
      .viz-details { width: 100%; max-height: 250px; border-left: none; border-top: 1px solid var(--border-color); }
      .viz-main { min-height: 400px; }
      .graph-controls { bottom: 10px; left: 10px; padding: 4px; gap: 4px; flex-wrap: wrap; max-width: calc(100% - 20px); }
      .graph-btn { min-width: 32px; height: 32px; font-size: 12px; }
      .controls-divider { height: 20px; }
      .sidebar-section { padding: 0.75rem; }
      .sidebar-section h3 { font-size: 0.8rem; margin-bottom: 0.5rem; }
      .filter-chips { gap: 4px; }
      .filter-chip { padding: 4px 8px; font-size: 11px; }
      .saved-badge { font-size: 0.75rem; }
    }

    @media (max-width: 480px) {
      .viz-header { padding: 0.5rem; }
      .view-toggle { display: none; }
      .viz-sidebar { max-height: 150px; }
      .graph-controls { gap: 3px; padding: 3px; }
      .graph-btn { min-width: 28px; height: 28px; font-size: 11px; padding: 0 6px; }
      .controls-divider { display: none; }
      #export-svg, #export-png { display: none; }
      .progress-container { width: 250px; }
      .projects-grid { grid-template-columns: 1fr; }
      .project-card { padding: 1rem; }
      .project-header h3 { font-size: 0.9rem; }
      .project-meta { flex-wrap: wrap; gap: 0.5rem; font-size: 0.75rem; }
      .project-actions { flex-wrap: wrap; }
      .repo-list { gap: 0.75rem; }
      .repo-card { padding: 1rem; }
      .repo-name { font-size: 0.9rem; }
    }
    .landing-main { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 1.5rem; }
    .input-options { display: flex; gap: 2rem; flex-wrap: wrap; justify-content: center; }
    .option-card { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 12px; padding: 2rem; text-align: center; width: 300px; transition: all 0.3s; position: relative; }
    .option-card:hover { border-color: var(--accent-blue); transform: translateY(-2px); }
    .option-card-pro { border-color: var(--accent-green); }
    .option-card-pro:hover { border-color: var(--accent-green); box-shadow: 0 0 20px rgba(63, 185, 80, 0.2); }
    .option-card-locked { border-color: var(--border-color); }
    .option-icon { font-size: 2rem; margin-bottom: 1rem; color: var(--accent-blue); }
    .option-icon svg { width: 32px; height: 32px; fill: currentColor; }
    .pro-icon { color: var(--accent-green); }
    .locked-icon { color: var(--text-secondary); opacity: 0.7; }
    .option-card h2 { margin-bottom: 0.5rem; }
    .option-card p { color: var(--text-secondary); margin-bottom: 1rem; font-size: 0.9rem; }
    .pro-badge { position: absolute; top: 1rem; right: 1rem; background: var(--accent-green); color: white; font-size: 0.7rem; font-weight: 700; padding: 0.25rem 0.5rem; border-radius: 4px; }
    .pro-badge-small { background: var(--accent-green); color: white; font-size: 0.65rem; font-weight: 700; padding: 0.15rem 0.4rem; border-radius: 3px; margin-left: 0.5rem; }
    .pricing-toggle { display: flex; gap: 0.5rem; margin: 1rem 0; }
    .plan-btn { flex: 1; padding: 0.5rem 0.75rem; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-secondary); cursor: pointer; font-size: 0.85rem; transition: all 0.2s; position: relative; }
    .plan-btn:hover { border-color: var(--accent-blue); }
    .plan-btn.active { background: var(--accent-blue); border-color: var(--accent-blue); color: white; }
    .save-badge { position: absolute; top: -8px; right: -8px; background: var(--accent-green); color: white; font-size: 0.6rem; font-weight: 700; padding: 0.2rem 0.4rem; border-radius: 4px; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.3); }
    .price-display { margin: 0.75rem 0; }
    .price-amount { font-size: 2rem; font-weight: 700; color: var(--text-primary); }
    .price-period { font-size: 1rem; color: var(--text-secondary); }
    .feature-list { list-style: none; text-align: left; margin: 1rem 0 1.5rem; padding: 0; }
    .feature-list li { color: var(--text-secondary); font-size: 0.85rem; padding: 0.3rem 0; padding-left: 1.5rem; position: relative; }
    .feature-list li::before { content: '✓'; position: absolute; left: 0; color: var(--accent-green); }
    .free-badge { display: inline-block; background: var(--bg-tertiary); color: var(--accent-green); font-size: 0.75rem; padding: 0.25rem 0.75rem; border-radius: 12px; margin-bottom: 1rem; }
    .cancel-note { color: var(--text-secondary); font-size: 0.75rem; margin-top: 0.75rem; }
    .privacy-note { color: var(--text-secondary); font-size: 0.75rem; margin: 0.75rem 0; opacity: 0.8; }
    .btn-glow { animation: glow 2s ease-in-out infinite; }
    @keyframes glow { 0%, 100% { box-shadow: 0 0 5px var(--accent-blue); } 50% { box-shadow: 0 0 20px var(--accent-blue); } }
    .success-message { background: rgba(63, 185, 80, 0.1); border: 1px solid var(--accent-green); color: var(--accent-green); padding: 1rem; border-radius: 8px; margin-bottom: 1rem; text-align: center; max-width: 500px; }
    .drop-zone { width: 100%; max-width: 600px; border: 2px dashed var(--border-color); border-radius: 12px; padding: 2.5rem; text-align: center; cursor: pointer; transition: all 0.2s; }
    .drop-zone:hover { border-color: var(--text-secondary); background: rgba(255,255,255,0.02); }
    .drop-zone.drag-over { border-color: var(--accent-blue); background: rgba(88,166,255,0.1); transform: scale(1.01); }
    .drop-icon { font-size: 2.5rem; margin-bottom: 0.75rem; opacity: 0.7; }
    .drop-zone p { color: var(--text-secondary); margin-bottom: 0.25rem; }
    .drop-hint { font-size: 0.8rem; color: var(--text-secondary); opacity: 0.6; }
    .landing-footer { text-align: center; color: var(--text-secondary); font-size: 0.85rem; margin-top: 2rem; }
    .projects-page { min-height: 100vh; padding: 1rem 2rem; }
    .page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
    .projects-actions { margin-bottom: 1.5rem; }
    .projects-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1rem; }
    .project-card { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 8px; padding: 1.5rem; transition: all 0.2s; }
    .project-card:hover { border-color: var(--accent-blue); transform: translateY(-2px); }
    .project-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.5rem; }
    .project-header h3 { font-size: 1rem; }
    .live-badge { background: var(--accent-green); color: white; font-size: 0.65rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: 3px; display: flex; align-items: center; gap: 0.25rem; }
    .live-badge::before { content: ''; width: 6px; height: 6px; background: white; border-radius: 50%; animation: pulse 1.5s infinite; }
    @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
    .project-card-header { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 16px; }
    .project-icon { width: 40px; height: 40px; background: var(--bg-tertiary); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: var(--accent-blue); flex-shrink: 0; }
    .project-info { flex: 1; min-width: 0; }
    .project-info h3 { font-size: 1rem; margin: 0 0 2px 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .project-branch { font-size: 0.75rem; color: var(--text-secondary); }
    .project-badges { display: flex; gap: 0.5rem; flex-shrink: 0; }
    .shared-badge { background: var(--accent-purple); color: white; font-size: 0.65rem; font-weight: 700; padding: 0.15rem 0.5rem; border-radius: 3px; }
    .project-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
    .stat-box { background: var(--bg-tertiary); border-radius: 6px; padding: 12px 8px; text-align: center; }
    .stat-number { display: block; font-size: 1.25rem; font-weight: 600; color: var(--text-primary); }
    .stat-label { font-size: 0.7rem; color: var(--text-secondary); text-transform: uppercase; letter-spacing: 0.5px; }
    .project-not-analyzed { background: var(--bg-tertiary); border-radius: 6px; padding: 16px; text-align: center; color: var(--text-secondary); font-size: 0.85rem; margin-bottom: 16px; }
    .project-footer { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; font-size: 0.8rem; }
    .project-updated { color: var(--text-secondary); }
    .needs-update { color: var(--accent-orange); font-weight: 500; }
    .project-actions { display: flex; gap: 0.5rem; }
    .share-link-row { display: flex; gap: 0.5rem; margin-bottom: 12px; }
    .share-link-input { flex: 1; padding: 0.5rem; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 4px; color: var(--text-secondary); font-size: 0.75rem; }
    .empty-projects { text-align: center; padding: 4rem 2rem; background: var(--bg-secondary); border: 2px dashed var(--border-color); border-radius: 12px; }
    .empty-projects .empty-icon { font-size: 3rem; margin-bottom: 1rem; opacity: 0.5; }
    .empty-projects h3 { margin: 0 0 0.5rem 0; color: var(--text-primary); }
    .empty-projects p { color: var(--text-secondary); margin-bottom: 1.5rem; max-width: 300px; margin-left: auto; margin-right: auto; }
    .no-projects { color: var(--text-secondary); text-align: center; padding: 3rem; }
    .btn { padding: 0.75rem 1.5rem; border-radius: 8px; border: none; cursor: pointer; font-size: 0.95rem; font-weight: 500; transition: all 0.2s; }
    .btn-primary { background: var(--accent-blue); color: white; }
    .btn-primary:hover { background: #4393e6; }
    .btn-github { background: #24292f; color: white; display: inline-flex; align-items: center; gap: 0.5rem; }
    .btn-github:hover { background: #32383f; }
    .btn-github svg { width: 20px; height: 20px; fill: currentColor; }
    .btn-secondary { background: var(--bg-tertiary); color: var(--text-primary); border: 1px solid var(--border-color); }
    .btn-text { background: transparent; color: var(--text-secondary); padding: 0.5rem 1rem; }
    .btn-large { padding: 1rem 2rem; font-size: 1.1rem; }
    .btn-small { padding: 0.5rem 1rem; font-size: 0.85rem; }
    .saved-badge { color: var(--accent-green); font-size: 0.85rem; font-weight: 500; display: flex; align-items: center; gap: 0.25rem; }
    .modal { position: fixed; inset: 0; background: rgba(0,0,0,0.8); display: flex; align-items: center; justify-content: center; z-index: 1000; }
    .modal-content { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 12px; padding: 2rem; text-align: center; max-width: 400px; }
    .pat-modal { text-align: left; }
    .pat-modal h2 { text-align: center; margin-bottom: 0.5rem; }
    .modal-description { color: var(--text-secondary); text-align: center; margin-bottom: 1.5rem; font-size: 0.9rem; }
    .pat-input { width: 100%; padding: 0.75rem 1rem; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-primary); font-family: monospace; font-size: 0.95rem; margin-bottom: 0.75rem; }
    .pat-input:focus { outline: none; border-color: var(--accent-blue); }
    .help-link { display: block; text-align: center; color: var(--accent-blue); font-size: 0.85rem; margin-bottom: 1rem; text-decoration: none; }
    .help-link:hover { text-decoration: underline; }
    .modal-error { color: var(--accent-red); font-size: 0.9rem; margin-bottom: 1rem; text-align: center; }
    .modal-buttons { display: flex; gap: 1rem; justify-content: center; }
    .modal-buttons .btn { min-width: 100px; }
    .device-code { font-family: monospace; font-size: 2rem; letter-spacing: 0.2em; padding: 1rem; background: var(--bg-tertiary); border-radius: 8px; margin: 1rem 0; }
    .modal-status { color: var(--text-secondary); margin: 1rem 0; }
    .repo-selector { min-height: 100vh; padding: 1rem 2rem; }
    .selector-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
    .user-info { display: flex; align-items: center; gap: 0.75rem; }
    .avatar { width: 32px; height: 32px; border-radius: 50%; }
    .search-bar { margin-bottom: 1.5rem; }
    .search-bar input { width: 100%; max-width: 400px; padding: 0.75rem 1rem; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 8px; color: var(--text-primary); }
    .repo-list { display: flex; flex-direction: column; gap: 1rem; }
    .repo-card { display: flex; align-items: center; justify-content: space-between; background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem 1.5rem; }
    .repo-card:hover { border-color: var(--accent-blue); }
    .repo-info h3 { margin-bottom: 0.25rem; }
    .repo-info p { color: var(--text-secondary); font-size: 0.9rem; margin-bottom: 0.5rem; }
    .repo-meta { display: flex; gap: 1rem; font-size: 0.85rem; color: var(--text-secondary); }
    .private { color: var(--accent-orange); }
    .loading-screen { min-height: 100vh; display: flex; align-items: center; justify-content: center; }
    .loading-content { text-align: center; }
    .progress-container { width: 300px; height: 8px; background: var(--bg-tertiary); border-radius: 4px; overflow: hidden; margin: 1.5rem 0; }
    .progress-bar { height: 100%; background: var(--accent-blue); width: 0%; transition: width 0.3s; }
    .progress-text { color: var(--text-secondary); font-size: 0.9rem; }
    .visualization { min-height: 100vh; display: flex; flex-direction: column; }
    .viz-header { display: flex; align-items: center; gap: 2rem; padding: 1rem 2rem; background: var(--bg-secondary); border-bottom: 1px solid var(--border-color); }
    .viz-stats { display: flex; gap: 1.5rem; margin-left: auto; color: var(--text-secondary); }
    .viz-container { flex: 1; display: grid; grid-template-columns: 250px 1fr 300px; min-height: 0; }
    .viz-sidebar, .viz-details { background: var(--bg-secondary); padding: 1rem; overflow-y: auto; }
    .viz-sidebar { border-right: 1px solid var(--border-color); }
    .viz-details { border-left: 1px solid var(--border-color); }
    .sidebar-section { margin-bottom: 1.5rem; }
    .sidebar-section h3 { font-size: 0.85rem; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 0.75rem; }
    .type-list, .hub-list { display: flex; flex-direction: column; gap: 0.5rem; }
    .type-item, .hub-item { display: flex; justify-content: space-between; font-size: 0.9rem; }
    .type-count, .hub-count { color: var(--text-secondary); }
    .timelapse-controls { display: flex; flex-direction: column; gap: 0.5rem; }
    .timelapse-controls input[type="range"] { width: 100%; }
    .viz-main { background: var(--bg-primary); overflow: hidden; display: flex; flex-direction: column; }
    .graph-controls { display: flex; align-items: center; gap: 1rem; padding: 0.75rem 1rem; background: var(--bg-secondary); border-bottom: 1px solid var(--border-color); }
    .graph-controls input { flex: 1; max-width: 250px; padding: 0.5rem 0.75rem; background: var(--bg-tertiary); border: 1px solid var(--border-color); border-radius: 6px; color: var(--text-primary); font-size: 0.9rem; }
    .control-buttons { display: flex; gap: 0.5rem; margin-left: auto; }
    .ctrl-btn { width: 32px; height: 32px; border-radius: 6px; border: 1px solid var(--border-color); background: var(--bg-tertiary); color: var(--text-secondary); cursor: pointer; font-size: 0.85rem; font-weight: 600; transition: all 0.2s; }
    .ctrl-btn:hover { border-color: var(--accent-blue); color: var(--text-primary); }
    .ctrl-btn.active { background: var(--accent-blue); border-color: var(--accent-blue); color: white; }
    .graph-container { flex: 1; width: 100%; }
    .graph-container svg { display: block; }
    .node.dimmed circle { opacity: 0.15; }
    .node.orphan circle { opacity: 0.35; stroke: var(--accent-red); stroke-dasharray: 4,3; }
    .error-message { background: rgba(248,81,73,0.1); border: 1px solid var(--accent-red); color: var(--accent-red); padding: 1rem; border-radius: 8px; margin-top: 1rem; }
    .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.7); display: flex; align-items: center; justify-content: center; z-index: 1000; backdrop-filter: blur(4px); }
    .modal { background: var(--bg-secondary); border: 1px solid var(--border-color); border-radius: 12px; padding: 2rem; max-width: 400px; width: 90%; position: relative; }
    .modal-close { position: absolute; top: 1rem; right: 1rem; background: none; border: none; color: var(--text-secondary); font-size: 1.5rem; cursor: pointer; line-height: 1; }
    .modal-close:hover { color: var(--text-primary); }
    .account-header { text-align: center; margin-bottom: 1.5rem; }
    .avatar-large { width: 80px; height: 80px; border-radius: 50%; margin-bottom: 0.75rem; }
    .account-header h2 { margin: 0; font-size: 1.25rem; }
    .account-email { color: var(--text-secondary); font-size: 0.9rem; margin: 0.25rem 0 0; }
    .account-section h3 { font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 1rem; text-transform: uppercase; letter-spacing: 0.05em; }
    .subscription-active { background: rgba(63,185,80,0.1); border: 1px solid var(--accent-green); border-radius: 8px; padding: 1rem; margin-bottom: 1rem; text-align: center; }
    .subscription-active .pro-badge { display: inline-block; margin-bottom: 0.5rem; }
    .subscription-active p { margin: 0; color: var(--text-secondary); font-size: 0.9rem; }
    .sub-period { margin-top: 0.25rem !important; font-size: 0.8rem !important; }
    .subscription-free { color: var(--text-secondary); margin-bottom: 1rem; }
    .billing-note { color: var(--text-secondary); font-size: 0.75rem; margin-top: 0.75rem; text-align: center; }
    .details-hint { color: var(--text-secondary); font-size: 0.9rem; }
    .node-details { }
    .node-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.5rem; }
    .node-color { width: 12px; height: 12px; border-radius: 50%; flex-shrink: 0; }
    .node-path { font-size: 0.8rem; color: var(--text-secondary); word-break: break-all; margin-bottom: 1rem; }
    .detail-section { margin-bottom: 1rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
    .detail-section h4 { font-size: 0.8rem; text-transform: uppercase; color: var(--text-secondary); margin-bottom: 0.5rem; }
    .detail-row { display: flex; justify-content: space-between; font-size: 0.9rem; padding: 0.25rem 0; }
    .exports-list { display: flex; flex-wrap: wrap; gap: 0.5rem; }
    .export-item { font-size: 0.8rem; padding: 0.25rem 0.5rem; background: var(--bg-tertiary); border-radius: 4px; color: var(--accent-green); }
  `;

  document.head.appendChild(style);
}

function addSettingsModalStyles() {
  if (document.getElementById('www-settings-styles')) return;

  const style = document.createElement('style');
  style.id = 'www-settings-styles';
  style.textContent = `
    .settings-modal {
      max-width: 480px;
      padding: 0;
    }
    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.25rem 1.5rem;
      border-bottom: 1px solid var(--border-color);
    }
    .modal-header h2 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
    }
    .modal-body {
      padding: 1.5rem;
      max-height: 60vh;
      overflow-y: auto;
    }
    .modal-footer {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding: 1rem 1.5rem;
      border-top: 1px solid var(--border-color);
      background: var(--bg-tertiary);
      border-radius: 0 0 12px 12px;
    }
    .form-section {
      margin-bottom: 1.5rem;
    }
    .form-section:last-child {
      margin-bottom: 0;
    }
    .form-section-title {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--text-secondary);
      margin-bottom: 1rem;
    }
    .form-group {
      margin-bottom: 1rem;
    }
    .form-group:last-child {
      margin-bottom: 0;
    }
    .form-group label {
      display: block;
      font-size: 0.9rem;
      font-weight: 500;
      color: var(--text-primary);
      margin-bottom: 0.5rem;
    }
    .form-group input[type="text"],
    .form-group textarea,
    .form-group select {
      width: 100%;
      padding: 0.625rem 0.875rem;
      background: var(--bg-tertiary);
      border: 1px solid var(--border-color);
      border-radius: 6px;
      color: var(--text-primary);
      font-size: 0.9rem;
      font-family: inherit;
      transition: border-color 0.2s;
    }
    .form-group input:focus,
    .form-group textarea:focus,
    .form-group select:focus {
      outline: none;
      border-color: var(--accent-blue);
    }
    .form-group textarea {
      resize: vertical;
      min-height: 80px;
    }
    .form-group select {
      cursor: pointer;
      appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238b949e' d='M2 4l4 4 4-4'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-position: right 0.75rem center;
      padding-right: 2.5rem;
    }
    .form-hint {
      font-size: 0.8rem;
      color: var(--text-secondary);
      margin-top: 0.375rem;
    }
    .checkbox-group {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }
    .checkbox-label {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
      font-size: 0.9rem;
      color: var(--text-primary);
    }
    .checkbox-label input[type="checkbox"] {
      width: 18px;
      height: 18px;
      accent-color: var(--accent-blue);
      cursor: pointer;
      flex-shrink: 0;
    }
    .checkbox-text {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }
    .checkbox-text span:first-child {
      font-weight: 500;
    }
    .checkbox-hint {
      font-size: 0.8rem;
      color: var(--text-secondary);
    }
    .settings-saved-toast {
      position: fixed;
      bottom: 2rem;
      left: 50%;
      transform: translateX(-50%);
      background: var(--accent-green);
      color: white;
      padding: 0.75rem 1.5rem;
      border-radius: 8px;
      font-size: 0.9rem;
      font-weight: 500;
      z-index: 1100;
      animation: toastIn 0.3s ease, toastOut 0.3s ease 2.7s forwards;
    }
    @keyframes toastIn {
      from { opacity: 0; transform: translateX(-50%) translateY(20px); }
      to { opacity: 1; transform: translateX(-50%) translateY(0); }
    }
    @keyframes toastOut {
      from { opacity: 1; transform: translateX(-50%) translateY(0); }
      to { opacity: 0; transform: translateX(-50%) translateY(20px); }
    }
  `;

  document.head.appendChild(style);
}

// Initialize app
document.addEventListener('DOMContentLoaded', init);
